import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { ComposedChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, Bar, ReferenceArea, ReferenceLine } from 'recharts';
import { AlertCircle, Settings, TrendingUp } from 'lucide-react';
import { isMetricSelected, getCosThreshold, useComponentMetrics, getMetricLabel, getMetricObjectiveClassName, libelleConversions, libelleTauxConversion, infobulleConversions } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import { useBulleAuSurvol } from './BulleSurvol';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';
import { marqueursDeNotes, InfobulleDeNotes } from './notes/marqueursDeNotes';
import { dateLocale } from '../utils/periodUtils';

// Classification des métriques : VALEURS (pour histogramme) vs RATIOS (pour courbe)
const VALUE_METRICS = ['clics', 'impressions', 'cout', 'conv', 'transactions', 'valeur_conv', 'interactions', 'reactions',
  'commentaires', 'enregistrements', 'partages', 'jaime_facebook', 'views'];
const RATIO_METRICS = ['ctr', 'cpc', 'cpa', 'taux_conv', 'cos', 'panier_moyen', 'cout_par_interaction', 'cout_par_reaction', 'roas', 'roi', 'vtr', 'cpv'];

// Mapping clé KPI (metricsUtils) → clé graphique (lineMetricConfig)
const METRIC_KEY_MAP = {
  clics: 'clicks',
  impressions: 'impressions',
  cout: 'cost',
  conv: 'conversions',
  transactions: 'transactions',
  valeur_conv: 'conversionsValue',
  interactions: 'interactions',
  reactions: 'reactions',
  commentaires: 'comments',
  enregistrements: 'saves',
  partages: 'shares',
  jaime_facebook: 'pageLikes',
  ctr: 'ctr',
  cpc: 'cpc',
  cpa: 'cpa',
  taux_conv: 'conversionRate',
  cos: 'cos',
  panier_moyen: 'panierMoyen',
  cout_par_interaction: 'coutParInteraction',
  cout_par_reaction: 'coutParReaction',
  roas: 'roas',
  roi: 'roi',
  views: 'views',
  vtr: 'vtr',
  cpv: 'cpv'
};

const PerformanceChart = ({
  autoCharts,
  formatNumber,
  KPICard,
  previousPeriodData,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  selectedAccount = null,
  granularity = 'day',
  onGranularityChange,
  selectedPeriod = 'LAST_7_DAYS',
  customStartDate,
  customEndDate,
  notes = [],
  compareEnabled = false,
  comparisonType = '',
  userRole = 'USER',
  isVisible = true,
  onToggleVisibility,
  // Props pour la visibilité client (persistée en base de données)
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'chart',
  /**
   * Ce que compte la métrique Conversions, pour la nommer.
   *
   * `null` partout sauf sur les écrans Meta, où le dashboard la transmet : ce composant est
   * partagé par Google Ads, Microsoft, Pinterest et TikTok, dont la métrique n'est pas
   * configurable. Le libellé reste alors « Conversions ».
   */
  conversionConfig = null,
  /**
   * Le jeton d'API, pour que la modale de configuration des métriques puisse charger les
   * conversions du compte et enregistrer le choix. Non utilisé ailleurs dans ce composant.
   */
  token = null
}) => {
  // Déterminer le label de comparaison
  const comparisonLabel = comparisonType === 'previous_year' ? 'année précédente' : 'période précédente';
  // Utiliser isVisible prop si onToggleVisibility est fourni, sinon état local
  const [localShowChart, setLocalShowChart] = useState(true);
  const showPerformanceChart = onToggleVisibility ? isVisible : localShowChart;
  const handleToggle = onToggleVisibility || (() => setLocalShowChart(!localShowChart));
  const [selectedLineMetric, setSelectedLineMetric] = useState('ctr'); // Métrique pour la courbe (RATIO)
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Configuration des métriques pour ce composant (utilise moduleId pour supporter msads_results, fbads_results, etc.)
  const componentMetrics = useComponentMetrics(moduleId, selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Filtrer les métriques disponibles selon celles activées dans "Configurer les métriques"
  const enabledMetrics = componentMetrics?.metrics || [];
  const availableValueMetrics = useMemo(() =>
    VALUE_METRICS.filter(m => enabledMetrics.includes(m)),
    [enabledMetrics]
  );
  // La courbe peut afficher TOUTES les métriques (VALEURS + RATIOS)
  const availableLineMetrics = useMemo(() =>
    [...VALUE_METRICS, ...RATIO_METRICS].filter(m => enabledMetrics.includes(m)),
    [enabledMetrics]
  );

  // État pour la métrique des barres (histogramme)
  /*
   * La bulle qui dit ce que compte la métrique Conversions, sur les deux cartes concernées.
   *
   * `title` ne convenait pas : le navigateur attend une à deux secondes, et sur une carte que
   * le pointeur traverse l'infobulle n'apparaît jamais. Le même constat qu'au tableau
   * Analytics de l'accueil, dont cette bulle est le mécanisme.
   */
  const { survoler: survolerSources, quitter: quitterSources, bulle: bulleSources } = useBulleAuSurvol();

  const [selectedBarMetric, setSelectedBarMetric] = useState('conversions');
  const lastValidPeriodRef = useRef(null);
  const lastAppliedChartConfigRef = useRef(null);
  /**
   * Le réglage que l'effet du `chartConfig` vient de poser, et que React n'a pas encore
   * répercuté dans l'état.
   *
   * Les deux effets s'exécutent dans le même cycle : le second lit donc l'état **d'avant**
   * le premier. Sans ce relais, une métrique de graphe parfaitement valide était jugée
   * indisponible — sur la foi de l'ancienne valeur — puis remplacée par le premier ratio venu.
   * Invisible sur Google Ads, dont l'état initial `ctr` fait partie des métriques ; systématique
   * partout où il n'en fait pas partie.
   */
  const chartConfigEnAttenteRef = useRef(null);

  // État pour la note survolée (affichage du tooltip)
  const [hoveredNote, setHoveredNote] = useState(null);
  const [noteTooltipPosition, setNoteTooltipPosition] = useState({ x: 0, y: 0 });

  // Appliquer le chartConfig du template quand il change
  useEffect(() => {
    const chartConfig = componentMetrics?.chartConfig;
    if (chartConfig) {
      // Vérifier si le chartConfig a changé
      const configKey = JSON.stringify(chartConfig);
      if (lastAppliedChartConfigRef.current !== configKey) {
        const { barMetric, lineMetric } = chartConfig;
        if (barMetric) setSelectedBarMetric(barMetric);
        if (lineMetric) setSelectedLineMetric(lineMetric);
        lastAppliedChartConfigRef.current = configKey;
        chartConfigEnAttenteRef.current = { barMetric, lineMetric };
      }
    }
  }, [componentMetrics?.chartConfig]);

  // Synchroniser les métriques sélectionnées avec les métriques disponibles
  useEffect(() => {
    /**
     * Tant qu'aucune métrique n'est connue, ne rien décider.
     *
     * Les métriques du composant arrivent après le premier rendu. Sans cette garde, cet effet
     * concluait que la métrique du graphe était « indisponible » — puisque *rien* ne l'était
     * encore — et la remplaçait par le premier ratio venu. L'effet qui applique le
     * `chartConfig` ne repasse pas ensuite : il ne réagit qu'au changement de configuration,
     * pas à l'arrivée des métriques. Le réglage choisi était donc perdu en silence, et le
     * graphe affichait une courbe que personne n'avait demandée.
     */
    if (availableLineMetrics.length === 0) return;

    // Ce que le graphe affichera réellement : le réglage qui vient d'être posé s'il y en a un,
    // l'état courant sinon. Le relais n'est consommé qu'une fois.
    const enAttente = chartConfigEnAttenteRef.current;
    chartConfigEnAttenteRef.current = null;
    const ligneCourante = enAttente?.lineMetric || selectedLineMetric;
    const barreCourante = enAttente?.barMetric || selectedBarMetric;

    // Pour la courbe : vérifier que la métrique sélectionnée est disponible (VALEUR ou RATIO)
    const currentLineKpiKey = Object.keys(METRIC_KEY_MAP).find(k => METRIC_KEY_MAP[k] === ligneCourante);
    if (currentLineKpiKey && !availableLineMetrics.includes(currentLineKpiKey)) {
      // Fallback : préférer un ratio, sinon une valeur
      const firstAvailableRatio = availableLineMetrics.find(m => RATIO_METRICS.includes(m));
      const firstAvailable = firstAvailableRatio || availableLineMetrics[0];
      if (firstAvailable) {
        setSelectedLineMetric(METRIC_KEY_MAP[firstAvailable]);
      }
    }

    // Pour l'histogramme : vérifier que la métrique sélectionnée est disponible (VALEUR ou RATIO)
    const currentBarKpiKey = Object.keys(METRIC_KEY_MAP).find(k => METRIC_KEY_MAP[k] === barreCourante);
    if (currentBarKpiKey && !availableLineMetrics.includes(currentBarKpiKey)) {
      // Sélectionner la première valeur disponible, sinon un ratio
      const firstAvailableValue = availableLineMetrics.find(m => VALUE_METRICS.includes(m));
      const firstAvailable = firstAvailableValue || availableLineMetrics[0];
      if (firstAvailable) {
        setSelectedBarMetric(METRIC_KEY_MAP[firstAvailable]);
      }
    }
  }, [availableLineMetrics, availableValueMetrics, selectedLineMetric, selectedBarMetric]);

  // Configuration des métriques disponibles pour la courbe
  const lineMetricConfig = {
    clicks: { label: 'Clics', color: '#82ca9d', unit: '', format: (val) => formatNumber(val) },
    impressions: { label: 'Impressions', color: '#6366f1', unit: '', format: (val) => formatNumber(val) },
    ctr: { label: 'CTR', color: '#14b8a6', unit: '%', format: (val) => formatNumber(val, 2) },
    conversionRate: { label: 'Taux conv.', color: '#f59e0b', unit: '%', format: (val) => formatNumber(val, 2) },
    cost: { label: 'Coût', color: '#ef4444', unit: '€', format: (val) => formatNumber(val, 2) },
    cpc: { label: 'CPC', color: '#8b5cf6', unit: '€', format: (val) => formatNumber(val, 2) },
    cpa: { label: 'CPA', color: '#f97316', unit: '€', format: (val) => formatNumber(val, 2) },
    conversionsValue: { label: 'Valeur Conv.', color: '#10b981', unit: '€', format: (val) => formatNumber(val, 2) },
    cos: { label: 'COS', color: '#ec4899', unit: '%', format: (val) => formatNumber(val, 1) },
    conversions: { label: 'Conv.', color: '#8884d8', unit: '', format: (val) => formatNumber(val) },
    interactions: { label: 'Interactions', color: '#06b6d4', unit: '', format: (val) => formatNumber(val) },
    coutParInteraction: { label: 'Coût/Inter.', color: '#0891b2', unit: '€', format: (val) => formatNumber(val, 2) },
    reactions: { label: 'Réactions', color: '#a855f7', unit: '', format: (val) => formatNumber(val) },
    comments: { label: 'Commentaires', color: '#14b8a6', unit: '', format: (val) => formatNumber(val) },
    saves: { label: 'Enregistrements', color: '#0ea5e9', unit: '', format: (val) => formatNumber(val) },
    shares: { label: 'Partages', color: '#f43f5e', unit: '', format: (val) => formatNumber(val) },
    pageLikes: { label: "J'aime (Facebook)", color: '#7c3aed', unit: '', format: (val) => formatNumber(val) },
    coutParReaction: { label: 'Coût/Réact.', color: '#9333ea', unit: '€', format: (val) => formatNumber(val, 2) },
    panierMoyen: { label: 'Panier moyen', color: '#6366f1', unit: '€', format: (val) => formatNumber(val, 2) },
    roas: { label: 'ROAS', color: '#22c55e', unit: '%', format: (val) => formatNumber(val, 0) },
    roi: { label: 'ROI', color: '#3b82f6', unit: 'x', format: (val) => formatNumber(val, 2) },
    views: { label: 'Vues', color: '#db2777', unit: '', format: (val) => formatNumber(val) },
    vtr: { label: 'Taux de vue', color: '#e11d48', unit: '%', format: (val) => formatNumber(val, 2) },
    cpv: { label: 'CPV', color: '#be123c', unit: '€', format: (val) => formatNumber(val, 3) }
  };

  // Fonction pour calculer la valeur de la métrique sélectionnée pour chaque point
  const getLineMetricValue = (d) => {
    switch (selectedLineMetric) {
      case 'clicks': return d.clicks || 0;
      case 'impressions': return d.impressions || 0;
      case 'ctr': return (d.impressions || 0) > 0 ? ((d.clicks || 0) / d.impressions * 100) : 0;
      case 'conversionRate': return d.clicks > 0 ? ((d.conversions || 0) / d.clicks * 100) : 0;
      case 'cost': return d.cost || 0;
      case 'cpc': return d.clicks > 0 ? (d.cost || 0) / d.clicks : 0;
      case 'cpa': return (d.conversions || 0) > 0 ? (d.cost || 0) / (d.conversions || 1) : 0;
      case 'conversionsValue': return d.conversionsValue || 0;
      case 'cos': {
        // Seuil minimum de 0.1€ pour éviter des COS aberrants avec des valeurs très petites
        const convValue = d.conversionsValue || 0;
        return convValue >= 0.1 ? ((d.cost || 0) / convValue * 100) : 0;
      }
      case 'conversions': return d.conversions || 0;
      case 'interactions': return d.interactions || 0;
      case 'coutParInteraction': return (d.interactions || 0) > 0 ? (d.cost || 0) / d.interactions : 0;
      case 'reactions': return d.reactions || 0;
      case 'comments': return d.comments || 0;
      case 'saves': return d.saves || 0;
      case 'shares': return d.shares || 0;
      /**
       * « J'aime (Facebook) » ne vaut que pour Facebook : Instagram a des abonnés, pas de fans
       * de page, et l'API n'expose rien d'équivalent pour ses placements. Sur un compte
       * exclusivement Instagram, la métrique reste à zéro — ce n'est pas une panne.
       */
      case 'pageLikes': return d.pageLikes || 0;
      case 'coutParReaction': return (d.reactions || 0) > 0 ? (d.cost || 0) / d.reactions : 0;
      case 'panierMoyen': return (d.conversions || 0) > 0 ? (d.conversionsValue || 0) / d.conversions : 0;
      case 'roas': return (d.cost || 0) > 0 ? ((d.conversionsValue || 0) / d.cost) * 100 : 0;
      case 'roi': return (d.cost || 0) > 0 ? ((d.conversionsValue || 0) - d.cost) / d.cost : 0;
      /**
       * Vidéo : le taux se rapporte aux impressions **vidéo**, que le backend fournit à part.
       * Le rapporter aux impressions totales donnerait un taux plusieurs fois trop bas.
       */
      case 'views': return d.views || 0;
      case 'vtr': return (d.videoImpressions || 0) > 0 ? ((d.views || 0) / d.videoImpressions * 100) : 0;
      case 'cpv': return (d.views || 0) > 0 ? (d.viewCost || 0) / d.views : 0;
      default: return d.clicks || 0;
    }
  };

  // Fonction pour calculer la valeur de la métrique des barres (identique à getLineMetricValue)
  const getBarMetricValue = (d) => {
    switch (selectedBarMetric) {
      case 'clicks': return d.clicks || 0;
      case 'impressions': return d.impressions || 0;
      case 'ctr': return (d.impressions || 0) > 0 ? ((d.clicks || 0) / d.impressions * 100) : 0;
      case 'conversionRate': return d.clicks > 0 ? ((d.conversions || 0) / d.clicks * 100) : 0;
      case 'cost': return d.cost || 0;
      case 'cpc': return d.clicks > 0 ? (d.cost || 0) / d.clicks : 0;
      case 'cpa': return (d.conversions || 0) > 0 ? (d.cost || 0) / (d.conversions || 1) : 0;
      case 'conversionsValue': return d.conversionsValue || 0;
      case 'cos': {
        // Seuil minimum de 0.1€ pour éviter des COS aberrants avec des valeurs très petites
        const convValue = d.conversionsValue || 0;
        return convValue >= 0.1 ? ((d.cost || 0) / convValue * 100) : 0;
      }
      case 'conversions': return d.conversions || 0;
      case 'interactions': return d.interactions || 0;
      case 'coutParInteraction': return (d.interactions || 0) > 0 ? (d.cost || 0) / d.interactions : 0;
      case 'reactions': return d.reactions || 0;
      case 'comments': return d.comments || 0;
      case 'saves': return d.saves || 0;
      case 'shares': return d.shares || 0;
      /**
       * « J'aime (Facebook) » ne vaut que pour Facebook : Instagram a des abonnés, pas de fans
       * de page, et l'API n'expose rien d'équivalent pour ses placements. Sur un compte
       * exclusivement Instagram, la métrique reste à zéro — ce n'est pas une panne.
       */
      case 'pageLikes': return d.pageLikes || 0;
      case 'coutParReaction': return (d.reactions || 0) > 0 ? (d.cost || 0) / d.reactions : 0;
      case 'panierMoyen': return (d.conversions || 0) > 0 ? (d.conversionsValue || 0) / d.conversions : 0;
      case 'roas': return (d.cost || 0) > 0 ? ((d.conversionsValue || 0) / d.cost) * 100 : 0;
      case 'roi': return (d.cost || 0) > 0 ? ((d.conversionsValue || 0) - d.cost) / d.cost : 0;
      /**
       * Vidéo : le taux se rapporte aux impressions **vidéo**, que le backend fournit à part.
       * Le rapporter aux impressions totales donnerait un taux plusieurs fois trop bas.
       */
      case 'views': return d.views || 0;
      case 'vtr': return (d.videoImpressions || 0) > 0 ? ((d.views || 0) / d.videoImpressions * 100) : 0;
      case 'cpv': return (d.views || 0) > 0 ? (d.viewCost || 0) / d.views : 0;
      default: return d.conversions || 0;
    }
  };

  // Debug des props reçues uniquement si nécessaire
  // console.log(`📊 [PerformanceChart] Props reçues - selectedPeriod: ${selectedPeriod}, customStart: ${customStartDate}, customEnd: ${customEndDate}`);

  // Fonction pour formater une date en local (sans conversion UTC)
  // Évite le décalage de -1 jour qui peut survenir avec toISOString()
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fonction pour normaliser une date vers le format ISO YYYY-MM-DD
  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    
    // Déjà au format ISO
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    
    // Format DD/MM/YY (nouveau format du backend)
    if (/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(dateStr)) {
      const parts = dateStr.split('/');
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      // Convertir YY en YYYY (25 → 2025, 26 → 2026)
      let year = parseInt(parts[2], 10);
      year = year >= 50 ? 1900 + year : 2000 + year; // 25 → 2025, 26 → 2026
      const result = `${year}-${month}-${day}`;
      
      console.log(`📅 [normalizeDate] DD/MM/YY: "${dateStr}" → "${result}"`);
      return result;
    }
    
    // Format DD/MM ou DD/MM/YYYY (ancien format)
    if (/^\d{1,2}\/\d{1,2}(\/\d{4})?$/.test(dateStr)) {
      const parts = dateStr.split('/');
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2] || new Date().getFullYear();
      const result = `${year}-${month}-${day}`;
      
      return result;
    }
    
    // Essayer de parser avec Date et convertir
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const result = formatLocalDate(date);

      // Debug pour les dates 2025
      if (result.includes('2025')) {
        console.log(`📅 [normalizeDate] Date parsing "${dateStr}" → "${result}"`);
      }

      return result;
    }
    
    return null;
  };

  /**
   * Hors de la plage réellement mesurée, une période comblée ne vaut pas zéro.
   *
   * Le comblement sert à redresser l'axe : une journée absente **entre** deux journées
   * mesurées est bien une journée sans diffusion, et zéro y est une mesure — on a interrogé,
   * il n'y avait rien. Avant la première journée mesurée, en revanche, il n'y a eu aucune
   * mesure du tout : un compte ChatGPT Ads ouvert en septembre et affiché sur l'année en
   * cours traçait une courbe plate à zéro depuis le 4 janvier, qui donnait à voir une
   * activité nulle là où il n'y avait rien à observer.
   *
   * Ces périodes gardent leur place sur l'axe — la période demandée reste lisible en entier —
   * mais portent `sansMesure`, qui annule leur point de courbe et leur barre. `connectNulls`
   * étant à `false`, le tracé s'interrompt au lieu de rejoindre l'origine.
   */
  const marquerLesBordsSansMesure = (points) => {
    let premiere = -1;
    let derniere = -1;
    points.forEach((point, index) => {
      if (point.__mesure) {
        if (premiere === -1) premiere = index;
        derniere = index;
      }
    });

    return points.map((point, index) => {
      const { __mesure, ...reste } = point;
      // Aucune mesure nulle part : `premiere` vaut -1 et aucun indice ne passe ce test.
      if (__mesure || (index > premiere && index < derniere)) return reste;
      return { ...reste, sansMesure: true };
    });
  };

  // Fonction pour combler les trous dans les données (ajouter les dates manquantes)
  const fillMissingDates = (data, startDate, endDate) => {
    // Normaliser les dates
    const normalizedStartDate = normalizeDate(startDate);
    const normalizedEndDate = normalizeDate(endDate);
    
    if (!normalizedStartDate || !normalizedEndDate) {
      console.warn('📅 [FILL-DATES] Dates invalides, retour données originales');
      return data || [];
    }
    
    // Minuit LOCAL des deux côtés : voir `dateLocale`. Avec minuit UTC, le curseur gagne une
    // heure au passage à l'heure d'hiver et la boucle rend un jour de moins.
    const start = dateLocale(normalizedStartDate);
    const end = dateLocale(normalizedEndDate);

    // Vérifier que les dates sont valides et dans le bon ordre
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
      console.warn(`📅 [FILL-DATES] Problème de dates: ${normalizedStartDate} -> ${normalizedEndDate}`);
      return data || [];
    }
    
    /**
     * Les journées comblées ne portent que les champs que les vraies journées portent.
     *
     * Ajouter `reach: 0` à l'aveugle transformait une série **sans** couverture — un chemin de
     * données qui ne la transporte pas — en une série de zéros, donc en une courbe plate posée
     * sur rien. Le comblement doit imiter la forme des données qu'il complète, pas en inventer
     * une.
     */
    const porteLaCouverture = (data || []).some(item => typeof item?.reach === 'number');

    // Créer une Map des données existantes avec clé normalisée
    const existingDates = new Map();
    if (data && data.length > 0) {
      for (const item of data) {
        const normalizedDate = normalizeDate(item.date);
        if (normalizedDate) {
          existingDates.set(normalizedDate, item);
        }
      }
    }
    
    // ÉTAPE 1: Générer TOUTES les dates de la période (priorité absolue)
    const filledData = [];
    const currentDate = new Date(start);

    while (currentDate <= end) {
      const dateStr = formatLocalDate(currentDate);
      
      if (existingDates.has(dateStr)) {
        // Utiliser les données existantes
        const existingItem = existingDates.get(dateStr);
        filledData.push({
          ...existingItem,
          date: dateStr,
          __mesure: true
        });
      } else {
        // Ajouter une ligne avec des zéros pour les dates manquantes
        filledData.push({
          date: dateStr,
          clicks: 0,
          conversions: 0,
          cost: 0,
          impressions: 0,
          // Un jour sans diffusion n'a touché personne : zéro est ici une mesure, pas un trou.
          // Mais seulement si les autres journées portent la couverture — voir ci-dessus.
          ...(porteLaCouverture ? { reach: 0, frequency: 0 } : {})
        });
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return marquerLesBordsSansMesure(filledData);
  };

  // Fonction pour formater les labels de date selon la granularité
  const formatDateLabel = (dateStr, granularity) => {
    if (!dateStr) return 'Date invalide';
    
    try {
      switch (granularity) {
        case 'week':
          return `Sem. ${dateStr}`;
        case 'month':
          // Le format attendu est "MM/YYYY" ou "YYYY-MM"
          if (dateStr.includes('/')) {
            return dateStr; // Déjà au bon format MM/YYYY
          } else if (dateStr.includes('-')) {
            const [year, month] = dateStr.split('-');
            return `${month}/${year}`;
          }
          return dateStr;
        case 'quarter':
          return dateStr; // Format "2024-Q1"
        case 'year':
          return dateStr;
        default: // 'day'
          // Si c'est déjà au format ISO (YYYY-MM-DD), traiter correctement
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            
            const currentYear = new Date().getFullYear();
            const dateYear = date.getFullYear();
            
            if (dateYear !== currentYear) {
              // Formatage avec l'année pour les données ISO
              const day = String(date.getDate()).padStart(2, '0');
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const year = String(dateYear).slice(-2);
              return `${day}/${month}/${year}`;
            } else {
              // Sans l'année pour l'année courante
              return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
            }
          }
          
          // Maintenant le backend envoie les dates au format DD/MM/YY, donc on peut les afficher directement
          if (/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(dateStr)) {
            return dateStr; // Format DD/MM/YY déjà correct
          }
          
          // Si c'est encore l'ancien format DD/MM (transition), garder l'ancienne logique temporairement
          if (/^\d{1,2}\/\d{1,2}$/.test(dateStr)) {
            const parts = dateStr.split('/');
            const month = parseInt(parts[1], 10);
            let assumedYear = month >= 7 ? 2025 : 2026;
            const year = String(assumedYear).slice(-2);
            return `${parts[0]}/${parts[1]}/${year}`;
          }
          
          // Fallback pour autres formats
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return dateStr;
          return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
      }
    } catch (error) {
      console.warn('Erreur formatage date:', dateStr, error);
      return dateStr; // Fallback to original string
    }
  };

  // Fonction pour agréger les données enrichies côté frontend (instantané)
  const aggregateEnrichedData = (enrichedData, targetGranularity) => {
    if (!enrichedData || !Array.isArray(enrichedData)) return [];
    
    // IMPORTANT: Utiliser la période sélectionnée au lieu de hardcoder 7 jours
    const periodDates = calculatePeriodDates(selectedPeriod, customStartDate, customEndDate);
    if (!periodDates) {
      console.log(`📊 [aggregateEnrichedData] Période invalide: ${selectedPeriod}`);
      return [];
    }
    
    const { startDate: startDateStr, endDate: endDateStr } = periodDates;
    return aggregateEnrichedDataInternal(enrichedData, targetGranularity, startDateStr, endDateStr);
  };

  // Fonction pour agréger avec période explicite (pour éviter le recalcul)
  const aggregateEnrichedDataWithPeriod = (enrichedData, targetGranularity, periodData) => {
    if (!periodData) return [];
    return aggregateEnrichedDataInternal(enrichedData, targetGranularity, periodData.startDate, periodData.endDate);
  };

  // Fonction interne pour agréger avec période fournie
  const aggregateEnrichedDataInternal = (enrichedData, targetGranularity, startDateStr, endDateStr) => {
    if (!enrichedData || !Array.isArray(enrichedData)) return [];
    console.log(`📊 [aggregateEnrichedData] Période ${selectedPeriod}: ${startDateStr} à ${endDateStr}`);
    
    // Debug des premières dates pour voir le format
    if (enrichedData && enrichedData.length > 0) {
      console.log(`📊 [aggregateEnrichedData] Premières dates brutes:`, enrichedData.slice(0, 5).map(item => item.date));
      
      // Vérifier s'il y a des dates 2025
      const dates2025 = enrichedData.filter(item => item.date && (
        item.date.includes('2025') || 
        item.date.includes('/25') ||
        (item.date.includes('/') && item.date.split('/')[2] === '2025')
      ));
      console.log(`📊 [aggregateEnrichedData] Données 2025 trouvées:`, dates2025.length, dates2025.slice(0, 3).map(item => item.date));
    }
    
    // Filtrer les données pour la période sélectionnée
    const filteredData = enrichedData.filter((item, index) => {
      const itemDate = normalizeDate(item.date);
      const isInPeriod = itemDate && itemDate >= startDateStr && itemDate <= endDateStr;
      
      // Debug pour les premières données
      if (index < 5) {
        console.log(`📊 [aggregateEnrichedData] Item ${index}: date="${item.date}" → normalized="${itemDate}" → inPeriod=${isInPeriod} (periode: ${startDateStr} à ${endDateStr})`);
      }
      
      
      return isInPeriod;
    });
    
    console.log(`📊 [aggregateEnrichedData] Données filtrées: ${enrichedData.length} → ${filteredData.length} items`);
    
    // Debug: montrer quelques dates d'entrée vs période
    const sampleDates = enrichedData.slice(0, 10).map(item => ({
      original: item.date,
      normalized: normalizeDate(item.date)
    }));
    console.log(`📊 [aggregateEnrichedData] Échantillon dates d'entrée:`, sampleDates);
    
    // CORRECTION: Si la granularité est 'day', retourner directement les données filtrées avec comblage des trous
    if (targetGranularity === 'day') {
      const filledData = fillMissingDates(filteredData, startDateStr, endDateStr);
      console.log(`📊 [aggregateEnrichedData] Mode jour: ${filteredData.length} → ${filledData.length} points`);
      return filledData;
    }
    
    // Pour les autres granularités, utiliser une agrégation manuelle basée sur les dates normalisées
    const groupedData = {};
    
    filteredData.forEach((item, index) => {
      const normalizedDate = normalizeDate(item.date);
      if (!normalizedDate) {
        console.warn(`⚠️ [AGGREG] Date normalization failed for: ${item.date}`);
        return;
      }
      
      const itemDateObj = new Date(normalizedDate);
      let periodKey, label;
      
      // Calculer la clé et le label selon la granularité
      switch (targetGranularity) {
        case 'week':
          // Calculer le lundi de la semaine
          const mondayDate = new Date(itemDateObj);
          const dayOfWeek = itemDateObj.getDay(); // 0 = dimanche, 1 = lundi
          const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Corriger pour que lundi = 0
          mondayDate.setDate(mondayDate.getDate() - daysToMonday);
          periodKey = formatLocalDate(mondayDate);
          label = formatLocalDate(mondayDate);
          break;
          
        case 'month':
          periodKey = `${itemDateObj.getFullYear()}-${String(itemDateObj.getMonth() + 1).padStart(2, '0')}`;
          label = `${String(itemDateObj.getMonth() + 1).padStart(2, '0')}/${itemDateObj.getFullYear()}`;
          break;
          
        case 'quarter':
          const quarter = Math.ceil((itemDateObj.getMonth() + 1) / 3);
          periodKey = `${itemDateObj.getFullYear()}-Q${quarter}`;
          label = `${itemDateObj.getFullYear()}-Q${quarter}`;
          break;
          
        case 'year':
          periodKey = `${itemDateObj.getFullYear()}`;
          label = `${itemDateObj.getFullYear()}`;
          break;
          
        default:
          periodKey = normalizedDate;
          label = normalizedDate;
      }
      
      if (!groupedData[periodKey]) {
        groupedData[periodKey] = {
          date: label,
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          conversionsValue: 0,
          interactions: 0,
          reactions: 0,
          // Les interactions sociales, que ce regroupement laissait tomber : la carte lisait
          // donc une série où le champ n'existait plus, et affichait zéro.
          comments: 0,
          saves: 0,
          shares: 0,
          pageLikes: 0,
          /**
           * Couverture et répétition : `null`, et jamais cumulées.
           *
           * Ce groupe couvre plusieurs journées. Additionner leurs couvertures compterait
           * plusieurs fois la personne revenue d'un jour à l'autre, et seul Meta saurait
           * recalculer la couverture d'une semaine — période par période. `null` dit
           * « pas de valeur ici », ce que la carte traduit par une absence de courbe.
           */
          reach: null,
          frequency: null
        };
      }

      // Agréger les métriques
      groupedData[periodKey].impressions += item.impressions || 0;
      groupedData[periodKey].clicks += item.clicks || 0;
      groupedData[periodKey].cost += item.cost || 0;
      groupedData[periodKey].conversions += item.conversions || 0;
      groupedData[periodKey].conversionsValue += item.conversionsValue || 0;
      groupedData[periodKey].interactions += item.interactions || 0;
      groupedData[periodKey].reactions += item.reactions || 0;
      groupedData[periodKey].comments += item.comments || 0;
      groupedData[periodKey].saves += item.saves || 0;
      groupedData[periodKey].shares += item.shares || 0;
      groupedData[periodKey].pageLikes += item.pageLikes || 0;

    });
    
    const sortedData = Object.values(groupedData).sort((a, b) => {
      // Tri chronologique intelligent selon la granularité
      const keys = Object.keys(groupedData);
      const keyA = keys.find(k => groupedData[k].date === a.date);
      const keyB = keys.find(k => groupedData[k].date === b.date);
      
      if (keyA && keyB) {
        if (targetGranularity === 'week' || targetGranularity === 'day') {
          return new Date(keyA) - new Date(keyB);
        } else {
          return keyA.localeCompare(keyB);
        }
      }
      
      return a.date.localeCompare(b.date);
    });
    
    console.log(`📊 [aggregateEnrichedData] Données agrégées ${targetGranularity}: ${filteredData.length} → ${sortedData.length} points`);
    
    // Debug spécial pour les semaines avec données
    if (targetGranularity === 'week') {
      const weeksWithData = sortedData.filter(week => week.clicks > 0 || week.impressions > 0);
      const weeksWithoutData = sortedData.filter(week => week.clicks === 0 && week.impressions === 0);
      console.log(`📊 [WEEK-RESULT] Total semaines: ${sortedData.length}, Avec données: ${weeksWithData.length}, Sans données: ${weeksWithoutData.length}`);
      console.log(`📊 [WEEK-RESULT] Semaines avec données:`, weeksWithData.slice(0, 10).map(w => `${w.date} (${w.clicks} clics)`));
      console.log(`📊 [WEEK-RESULT] Premières semaines sans données:`, weeksWithoutData.slice(0, 5).map(w => w.date));
    }
    
    // CORRECTION: Combler les trous pour les autres granularités aussi
    if (targetGranularity !== 'day') {
      const filledData = fillMissingPeriods(sortedData, targetGranularity, startDateStr, endDateStr);
      console.log(`📊 [aggregateEnrichedData] Données après remplissage ${targetGranularity}: ${sortedData.length} → ${filledData.length} points`);
      return filledData;
    }
    
    return sortedData;
  };

  // Fonction spécialisée pour combler les semaines manquantes
  const fillMissingWeeksFromAggregatedData = (weekData, startDateStr, endDateStr) => {
    if (!weekData.length) return [];
    
    const existingWeeks = new Map();
    weekData.forEach(week => {
      existingWeeks.set(week.date, week);
    });
    
    
    // Générer TOUTES les semaines de l'année en itérant jour par jour
    const allWeeks = [];
    const currentDate = dateLocale(startDateStr);
    const endDate = dateLocale(endDateStr);
    const seenWeeks = new Set();
    
    while (currentDate <= endDate) {
      // Calculer le lundi de la semaine (EXACTEMENT la même logique que l'agrégation)
      // CORRECTION: reproduire EXACTEMENT les lignes 295, 302-306 de l'agrégation
      const normalizedDate = formatLocalDate(currentDate); // Comme ligne 295
      const itemDateObj = new Date(normalizedDate); // Comme ligne 295
      const mondayDate = new Date(itemDateObj); // Comme ligne 302
      const dayOfWeek = itemDateObj.getDay(); // Comme ligne 303
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Comme ligne 304
      mondayDate.setDate(mondayDate.getDate() - daysToMonday); // Comme ligne 305
      const weekKey = formatLocalDate(mondayDate); // Comme ligne 306
      
      
      // Ajouter cette semaine si pas encore vue
      if (!seenWeeks.has(weekKey)) {
        seenWeeks.add(weekKey);
        
        if (existingWeeks.has(weekKey)) {
          // Utiliser les données existantes
          allWeeks.push({ ...existingWeeks.get(weekKey), __mesure: true });
        } else {
          // Créer une semaine vide
          allWeeks.push({
            date: weekKey,
            impressions: 0,
            clicks: 0,
            cost: 0,
            conversions: 0,
            conversionsValue: 0
          });
        }
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    // Trier par date
    allWeeks.sort((a, b) => a.date.localeCompare(b.date));
    
    return marquerLesBordsSansMesure(allWeeks);
  };

  // Fonction pour combler les trous dans les données pour toutes les granularités
  const fillMissingPeriods = (data, granularity, startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return data;


    // CORRECTION: Pour les semaines, utiliser une approche différente
    // car l'iteration par semaines ne génère pas les mêmes clés que l'agrégation
    if (granularity === 'week') {
      return fillMissingWeeksFromAggregatedData(data, startDateStr, endDateStr);
    }

    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);
    const existingPeriods = new Map();

    // Indexer les données existantes
    data.forEach(item => {
      existingPeriods.set(item.date, item);
    });

    const filledData = [];

    // CORRECTION: Pour mois/trimestre/année, commencer au 1er de la période
    // pour éviter de sauter des périodes quand la date ne commence pas le 1er
    let currentDate;
    if (granularity === 'month') {
      currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    } else if (granularity === 'quarter') {
      // Commencer au 1er jour du trimestre (mois 0, 3, 6, ou 9)
      const quarterStartMonth = Math.floor(startDate.getMonth() / 3) * 3;
      currentDate = new Date(startDate.getFullYear(), quarterStartMonth, 1);
    } else if (granularity === 'year') {
      // Commencer au 1er janvier de l'année
      currentDate = new Date(startDate.getFullYear(), 0, 1);
    } else {
      currentDate = new Date(startDate);
    }

    while (currentDate <= endDate) {
      let periodKey, label;

      switch (granularity) {
        case 'month':
          periodKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
          label = `${String(currentDate.getMonth() + 1).padStart(2, '0')}/${currentDate.getFullYear()}`;

          // Avancer au 1er du mois suivant
          currentDate.setMonth(currentDate.getMonth() + 1);
          currentDate.setDate(1);
          break;
          
        case 'quarter':
          const quarter = Math.ceil((currentDate.getMonth() + 1) / 3);
          periodKey = `${currentDate.getFullYear()}-Q${quarter}`;
          label = `${currentDate.getFullYear()}-Q${quarter}`;

          // Avancer au 1er du trimestre suivant
          currentDate.setMonth(currentDate.getMonth() + 3);
          currentDate.setDate(1);
          break;

        case 'year':
          periodKey = `${currentDate.getFullYear()}`;
          label = `${currentDate.getFullYear()}`;

          // Avancer au 1er janvier de l'année suivante
          currentDate.setFullYear(currentDate.getFullYear() + 1);
          currentDate.setMonth(0);
          currentDate.setDate(1);
          break;
          
        default:
          return data;
      }
      
      // Ajouter la période (existante ou vide)
      if (existingPeriods.has(label)) {
        const existingData = existingPeriods.get(label);
        filledData.push({ ...existingData, __mesure: true });
        console.log(`📅 [fillMissingPeriods] Trouvé données existantes pour ${label}: ${existingData.clicks} clics`);
      } else {
        filledData.push({
          date: label,
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          conversionsValue: 0
        });
        console.log(`📅 [fillMissingPeriods] Ajouté données vides pour ${label}`);
      }
    }
    
    console.log(`📅 [fillMissingPeriods] Résultat ${granularity}:`, filledData.map(d => d.date));
    return marquerLesBordsSansMesure(filledData);
  };

  // Debug initial

  // Fonction pour calculer les dates selon la période sélectionnée
  const calculatePeriodDates = (period, customStart, customEnd) => {
    if (period === 'CUSTOM') {
      // Si les dates personnalisées ne sont pas encore définies, retourner null
      // Le composant parent devra gérer ce cas
      if (!customStart || !customEnd) {
        console.log(`📅 [CUSTOM] Dates non définies, retour null`);
        return null;
      }
      
      return { 
        startDate: customStart, 
        endDate: customEnd 
      };
    }

    const today = new Date();
    let startDate, endDate;

    switch (period) {
      case 'TODAY':
        startDate = endDate = formatLocalDate(today);
        break;
      case 'YESTERDAY':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = formatLocalDate(yesterday);
        break;
      case 'LAST_7_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 6);
        break;
      case 'LAST_14_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 13);
        break;
      case 'LAST_30_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 29);
        break;
      case 'LAST_60_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 59);
        break;
      case 'LAST_90_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 89);
        break;
      case 'LAST_180_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 179);
        break;
      case 'LAST_365_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 364);
        break;
      case 'THIS_MONTH':
        // CORRECTION: Calculer le mois en cours correctement avec gestion du fuseau horaire
        const thisYear = today.getFullYear();
        const thisMonthIndex = today.getMonth(); // 0-11
        const thisMonth = String(thisMonthIndex + 1).padStart(2, '0'); // +1 car les mois JS sont 0-indexés
        
        // Du 1er du mois jusqu'à hier
        startDate = `${thisYear}-${thisMonth}-01`;
        
        const yesterdayDate = new Date(today);
        yesterdayDate.setDate(yesterdayDate.getDate() - 1);
        endDate = formatLocalDate(yesterdayDate);

        console.log(`📅 [THIS_MONTH] Calcul corrigé: aujourd'hui=${formatLocalDate(today)}, ce mois=${startDate} à ${endDate}`);
        break;
      case 'LAST_MONTH':
        // Calculer le mois précédent correctement avec gestion du fuseau horaire
        let lastMonthYear = today.getFullYear();
        let lastMonthIndex = today.getMonth() - 1;
        
        // Si nous sommes en janvier (0), le mois précédent est décembre de l'année précédente
        if (lastMonthIndex < 0) {
          lastMonthYear = today.getFullYear() - 1;
          lastMonthIndex = 11; // Décembre
        }
        
        // CORRECTION: Utiliser des chaînes ISO pour éviter les problèmes de fuseau horaire
        const startMonth = String(lastMonthIndex + 1).padStart(2, '0'); // +1 car les mois JS sont 0-indexés
        const startDateStr = `${lastMonthYear}-${startMonth}-01`;
        
        // Calculer le dernier jour du mois
        const tempDate = new Date(lastMonthYear, lastMonthIndex + 1, 0);
        const lastDay = tempDate.getDate();
        const endDateStr = `${lastMonthYear}-${startMonth}-${String(lastDay).padStart(2, '0')}`;
        
        startDate = startDateStr;
        endDate = endDateStr;

        console.log(`📅 [LAST_MONTH] Calcul corrigé: aujourd'hui=${formatLocalDate(today)}, mois précédent=${startDate} à ${endDate}`);
        break;
      case 'LAST_YEAR':
        // CORRECTION: Calculer l'année précédente correctement avec gestion du fuseau horaire
        const lastYear = today.getFullYear() - 1;
        startDate = `${lastYear}-01-01`; // 1er janvier de l'année dernière
        endDate = `${lastYear}-12-31`; // 31 décembre de l'année dernière
        console.log(`📅 [LAST_YEAR] Calcul corrigé: aujourd'hui=${formatLocalDate(today)}, année précédente=${startDate} à ${endDate}`);
        break;
      case 'THIS_YEAR':
        // CORRECTION: Calculer l'année en cours correctement avec gestion du fuseau horaire
        const currentYear = today.getFullYear();
        startDate = `${currentYear}-01-01`; // 1er janvier de cette année

        const yesterdayForYear = new Date(today);
        yesterdayForYear.setDate(yesterdayForYear.getDate() - 1);
        endDate = formatLocalDate(yesterdayForYear); // Jusqu'à hier

        console.log(`📅 [THIS_YEAR] Calcul corrigé: aujourd'hui=${formatLocalDate(today)}, cette année=${startDate} à ${endDate}`);
        break;
      default:
        // Default à 7 derniers jours
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 6);
    }

    return {
      startDate: typeof startDate === 'string' ? startDate : formatLocalDate(startDate),
      endDate: typeof endDate === 'string' ? endDate : formatLocalDate(endDate)
    };
  };

  // Fonction pour générer des données vides avec dates selon la période
  const generateEmptyData = () => {
    const periodDates = calculatePeriodDates(selectedPeriod, customStartDate, customEndDate);
    if (!periodDates) return [];

    const data = [];
    const start = dateLocale(periodDates.startDate);
    const end = dateLocale(periodDates.endDate);

    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      data.push({
        date: formatLocalDate(date),
        clicks: 0,
        conversions: 0,
        cost: 0,
        impressions: 0
      });
    }
    return data;
  };

  // Mettre en cache les données pour éviter les recalculs multiples
  const memoizedChartData = useMemo(() => {
    // Calculer la période effective
    const currentPeriodData = calculatePeriodDates(selectedPeriod, customStartDate, customEndDate);

    // Si période personnalisée sans dates valides, utiliser la dernière période valide
    let effectivePeriodData = currentPeriodData;
    if (selectedPeriod === 'CUSTOM' && !currentPeriodData && lastValidPeriodRef.current) {
      effectivePeriodData = lastValidPeriodRef.current;
    } else if (currentPeriodData && selectedPeriod !== 'CUSTOM') {
      // Sauvegarder la nouvelle période valide (sauf pour CUSTOM)
      lastValidPeriodRef.current = currentPeriodData;
    }
    
    // Si aucune période valide, utiliser 7 derniers jours par défaut
    if (!effectivePeriodData) {
      const today = new Date();
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() - 1);
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 6);

      effectivePeriodData = {
        startDate: formatLocalDate(startDate),
        endDate: formatLocalDate(endDate)
      };
    }

    // Trouver le graphique de performance
    const performanceChart = autoCharts?.charts?.find(chart => chart.id === 'real-performance-timeline');

    // Générer les données de base pour le graphique
    let chartData;

    if (!performanceChart || !performanceChart.data || performanceChart.data.length === 0) {
      // Générer des données vides avec la période effective
      const data = [];
      const start = dateLocale(effectivePeriodData.startDate);
      const end = dateLocale(effectivePeriodData.endDate);

      for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
        data.push({
          date: formatLocalDate(date),
          clicks: 0,
          conversions: 0,
          cost: 0,
          impressions: 0,
          // Rien n'a été mesuré sur la période : l'axe est dressé, mais sans tracé.
          sansMesure: true
        });
      }
      chartData = data;
    } else {
      // Utiliser la période effective calculée
      const { startDate: startDateStr, endDate: endDateStr } = effectivePeriodData;

      // Filtrer les données pour ne garder que celles dans la période effective
      const filteredData = performanceChart.data.filter(item => {
        const normalizedDate = normalizeDate(item.date);
        return normalizedDate && normalizedDate >= startDateStr && normalizedDate <= endDateStr;
      });

      // Combler les trous dans les données filtrées avec la période effective
      chartData = fillMissingDates(filteredData, startDateStr, endDateStr);
    }

    // Retourner les données de base (le filtrage par campagne se fait dans le second useMemo)
    const rawDataAccount = autoCharts.rawData ? Object.values(autoCharts.rawData)[0] : null;
    
    // Chercher les données détaillées jour par jour avec information de campagne
    let detailedData = null;
    if (rawDataAccount) {
      // Chercher dans TOUTES les sources possibles
      const possibleDataSources = [
        'data', 'results', 'rawResults', 'dailyData', 'timeSeriesData', 
        'detailedData', 'performanceData', 'campaignData', 'rawData'
      ];
      
      for (const source of possibleDataSources) {
        if (rawDataAccount[source] && Array.isArray(rawDataAccount[source]) && rawDataAccount[source].length > 0) {
          const firstItem = rawDataAccount[source][0];
          
          // Vérifier si cette source contient des données par jour ET par campagne
          if (firstItem.date && (firstItem.campaign_id || firstItem.campaignId || firstItem.campaign_name || firstItem.campaignName)) {
            detailedData = rawDataAccount[source];
            break;
          }
        }
      }
      
      // Si toujours pas trouvé, chercher dans les propriétés imbriquées
      if (!detailedData) {
        for (const key of Object.keys(rawDataAccount)) {
          const value = rawDataAccount[key];
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            for (const subKey of Object.keys(value)) {
              const subValue = value[subKey];
              if (Array.isArray(subValue) && subValue.length > 0) {
                const firstItem = subValue[0];
                if (firstItem && firstItem.date && (firstItem.campaign_id || firstItem.campaignId)) {
                  detailedData = subValue;
                  break;
                }
              }
            }
            if (detailedData) break;
          }
        }
      }
      
      if (detailedData && detailedData.length > 0) {
      } else {
      }
    }
    
    // Si on a des données détaillées avec campagne, faire un vrai filtrage
    if (detailedData && detailedData.length > 0) {
      const campaignIdField = detailedData[0].campaign_id !== undefined ? 'campaign_id' : 'campaignId';
      const campaignNameField = detailedData[0].campaign_name !== undefined ? 'campaign_name' : 'campaignName';
      const campaignTypeField = detailedData[0].campaign_type !== undefined ? 'campaign_type' : 'campaignType';
      
      // Filtrer les données par campagne et/ou type de campagne
      const filteredDetailedData = detailedData.filter(row => {
        const rowCampaignId = row[campaignIdField];
        const rowCampaignName = row[campaignNameField];
        const rowCampaignType = row[campaignTypeField];
        
        let matchesCampaign = true;
        let matchesCampaignType = true;
        
        if (selectedCampaignFilter) {
          matchesCampaign = rowCampaignId === selectedCampaignFilter.id || 
                           String(rowCampaignId) === String(selectedCampaignFilter.id) ||
                           rowCampaignId === selectedCampaignFilter.campaignId ||
                           rowCampaignName === selectedCampaignFilter.name;
        }
        
        if (selectedCampaignTypeFilter) {
          matchesCampaignType = rowCampaignType === selectedCampaignTypeFilter.type ||
                               String(rowCampaignType) === String(selectedCampaignTypeFilter.type) ||
                               rowCampaignType === selectedCampaignTypeFilter.name ||
                               String(rowCampaignType) === String(selectedCampaignTypeFilter.name);
        }
        
        return matchesCampaign && matchesCampaignType;
      });
      
      
      if (filteredDetailedData.length > 0) {
        // Agréger par jour
        const dailyData = new Map();
        
        filteredDetailedData.forEach(row => {
          const date = row.date;
          if (!dailyData.has(date)) {
            dailyData.set(date, {
              date: date,
              impressions: 0,
              clicks: 0,
              cost: 0,
              conversions: 0
            });
          }
          
          const dayData = dailyData.get(date);
          dayData.impressions += parseInt(row.impressions) || 0;
          dayData.clicks += parseInt(row.clicks) || 0;
          dayData.cost += parseFloat(row.cost) || parseFloat(row.spend) || 0;
          dayData.conversions += parseInt(row.conversions) || 0;
        });
        
        // Convertir en array trié
        const sortedData = Array.from(dailyData.values())
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .map(day => ({
            ...day,
            date: day.date // Garder le format de date original
          }));
          
        // Combler les trous dans les données filtrées
        if (sortedData.length > 0) {
          // CORRECTION: Utiliser la période sélectionnée au lieu des dates des données filtrées
          const periodDates = calculatePeriodDates(selectedPeriod, customStartDate, customEndDate);
          const startDate = periodDates ? periodDates.startDate : sortedData[0].date;
          const endDate = periodDates ? periodDates.endDate : sortedData[sortedData.length - 1].date;
          chartData = fillMissingDates(sortedData, startDate, endDate);
        } else {
          // Si aucune donnée filtrée, générer des données vides pour la période complète
          const periodDates = calculatePeriodDates(selectedPeriod, customStartDate, customEndDate);
          if (periodDates) {
            chartData = fillMissingDates([], periodDates.startDate, periodDates.endDate);
          } else {
            // Fallback vers les données de base
            const baseData = performanceChart?.data || chartData;
            chartData = baseData.map(day => ({
              ...day,
              clicks: 0,
              conversions: 0,
              cost: 0,
              impressions: 0
            }));
          }
        }
        
      } else {
        // Créer des données vides pour le graphique avec la période complète
        const periodDates = calculatePeriodDates(selectedPeriod, customStartDate, customEndDate);
        if (periodDates) {
          chartData = fillMissingDates([], periodDates.startDate, periodDates.endDate);
        } else {
          // Fallback vers les données de base
          const baseData = performanceChart?.data || chartData;
          chartData = baseData.map(day => ({
            ...day,
            clicks: 0,
            conversions: 0,
            cost: 0,
            impressions: 0
          }));
        }
      }
    } else {
      // Fallback: utiliser l'ancienne méthode proportionnelle si pas de données détaillées
      
      let campaignsData = autoCharts.rawData ? 
        Object.values(autoCharts.rawData).find(data => data.campaigns)?.campaigns || [] : [];
        
      let filteredCampaigns = campaignsData;
      
      // Filtrer par campagne si sélectionnée
      if (selectedCampaignFilter) {
        filteredCampaigns = filteredCampaigns.filter(campaign => 
          campaign.id === selectedCampaignFilter.id || 
          campaign.campaignId === selectedCampaignFilter.id ||
          String(campaign.id) === String(selectedCampaignFilter.id) ||
          campaign.name === selectedCampaignFilter.name
        );
      }
      
      // Filtrer par type de campagne si sélectionné
      if (selectedCampaignTypeFilter) {
        filteredCampaigns = filteredCampaigns.filter(campaign => {
          const campaignType = campaign.type || campaign.campaignType || campaign.channelType ||
                              campaign.advertising_channel_type || campaign.advertisingChannelType;
          return campaignType === selectedCampaignTypeFilter.type ||
                 String(campaignType) === String(selectedCampaignTypeFilter.type) ||
                 campaignType === selectedCampaignTypeFilter.name ||
                 String(campaignType) === String(selectedCampaignTypeFilter.name);
        });
      }
      
      
      if (filteredCampaigns.length > 0) {
        const originalData = performanceChart?.data || chartData;
        const totalOriginal = originalData.reduce((acc, day) => ({
          clicks: acc.clicks + (day.clicks || 0),
          conversions: acc.conversions + (day.conversions || 0),
          cost: acc.cost + (day.cost || 0),
          impressions: acc.impressions + (day.impressions || 0)
        }), { clicks: 0, conversions: 0, cost: 0, impressions: 0 });
        
        const totalFiltered = filteredCampaigns.reduce((acc, campaign) => ({
          clicks: acc.clicks + (campaign.clicks || 0),
          conversions: acc.conversions + (campaign.conversions || 0),
          cost: acc.cost + (campaign.cost || 0),
          impressions: acc.impressions + (campaign.impressions || 0)
        }), { clicks: 0, conversions: 0, cost: 0, impressions: 0 });
        
        const clicksRatio = totalOriginal.clicks > 0 ? totalFiltered.clicks / totalOriginal.clicks : 0;
        const conversionsRatio = totalOriginal.conversions > 0 ? totalFiltered.conversions / totalOriginal.conversions : 0;
        const costRatio = totalOriginal.cost > 0 ? totalFiltered.cost / totalOriginal.cost : 0;
        const impressionsRatio = totalOriginal.impressions > 0 ? totalFiltered.impressions / totalOriginal.impressions : 0;
        
        
        chartData = originalData.map(day => ({
          ...day,
          clicks: Math.round((day.clicks || 0) * clicksRatio),
          conversions: Math.round((day.conversions || 0) * conversionsRatio),
          cost: parseFloat(((day.cost || 0) * costRatio).toFixed(2)),
          impressions: Math.round((day.impressions || 0) * impressionsRatio)
        }));
      } else {
        // Si aucune campagne ne correspond, montrer un graphique vide avec toutes les dates
        const periodDates = calculatePeriodDates(selectedPeriod, customStartDate, customEndDate);
        if (periodDates) {
          chartData = fillMissingDates([], periodDates.startDate, periodDates.endDate);
        } else {
          // Fallback vers les données de base
          const baseData = performanceChart?.data || chartData;
          chartData = baseData.map(day => ({
            ...day,
            clicks: 0,
            conversions: 0,
            cost: 0,
            impressions: 0
          }));
        }
      }
    }

    return { chartData, effectivePeriodData };
  }, [autoCharts, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter, selectedCampaignTypeFilter]);

  // Mettre en cache les données pour le graphique et les KPI cards
  const { finalChartData, kpiData, filteredPreviousPeriodData } = useMemo(() => {
    const enrichedData = autoCharts?.rawData ?
      Object.values(autoCharts.rawData)[0]?.enrichedDailyData : null;
    
    let finalData = memoizedChartData.chartData;
    const periodData = memoizedChartData.effectivePeriodData;
    
    if (enrichedData && enrichedData.length > 0) {
      // Utiliser periodData au lieu de recalculer
      const aggregatedData = aggregateEnrichedDataWithPeriod(enrichedData, granularity, periodData);

      // Si filtrage par campagne, utiliser les données réelles de la campagne
      if (selectedCampaignFilter) {
        // Récupérer les données quotidiennes par campagne
        const dailyDataByCampaign = autoCharts.rawData ?
          Object.values(autoCharts.rawData).find(data => data.dailyDataByCampaign)?.dailyDataByCampaign || {} : {};

        // Trouver l'ID et le nom de la campagne sélectionnée
        const campaignId = selectedCampaignFilter.id || selectedCampaignFilter.campaignId;
        const campaignIdStr = String(campaignId);
        const campaignName = selectedCampaignFilter.name || selectedCampaignFilter.campaignName;

        // Chercher les données de la campagne (essayer différentes clés: id, id en string, nom)
        const campaignDailyData = dailyDataByCampaign[campaignId] ||
                                dailyDataByCampaign[campaignIdStr] ||
                                dailyDataByCampaign[campaignName] ||
                                Object.entries(dailyDataByCampaign).find(([key]) =>
                                  String(key) === campaignIdStr || key === campaignName
                                )?.[1] || [];

        if (campaignDailyData && campaignDailyData.length > 0) {
          // Enrichir les données quotidiennes de la campagne avec les clés de période
          const enrichedCampaignData = campaignDailyData.map(dayData => {
            const date = new Date(dayData.date);
            const weekStart = new Date(date);
            const dayOfWeek = date.getDay();
            const daysToMonday = (dayOfWeek === 0 ? 6 : dayOfWeek - 1);
            weekStart.setDate(date.getDate() - daysToMonday);
            weekStart.setHours(0, 0, 0, 0);

            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const quarter = Math.ceil((date.getMonth() + 1) / 3);
            const quarterKey = `${date.getFullYear()}-Q${quarter}`;
            const yearKey = String(date.getFullYear());

            const weekStartStr = formatLocalDate(weekStart);
            return {
              ...dayData,
              periods: {
                day: dayData.date,
                week: weekStartStr,
                month: monthKey,
                quarter: quarterKey,
                year: yearKey
              },
              labels: {
                day: date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
                week: `Sem. ${weekStartStr}`,
                month: `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`,
                quarter: quarterKey,
                year: yearKey
              }
            };
          });

          // Agréger selon la granularité
          finalData = aggregateEnrichedDataWithPeriod(enrichedCampaignData, granularity, periodData);
        } else {
          // Fallback: données vides si pas de données pour cette campagne
          finalData = aggregatedData.map(period => ({
            ...period,
            impressions: 0,
            clicks: 0,
            cost: 0,
            conversions: 0,
            conversionsValue: 0
          }));
        }
      } else if (selectedCampaignTypeFilter) {
        // Filtrer par type de campagne côté frontend
        const dailyDataByCampaign = autoCharts.rawData ?
          Object.values(autoCharts.rawData).find(data => data.dailyDataByCampaign)?.dailyDataByCampaign || {} : {};
        const campaignsData = autoCharts.rawData ?
          Object.values(autoCharts.rawData).find(data => data.campaigns)?.campaigns || [] : [];

        // Trouver les IDs des campagnes du type sélectionné
        const filteredCampaignIds = campaignsData
          .filter(campaign => {
            const campaignType = campaign.type || campaign.campaignType || campaign.channelType ||
                                campaign.advertising_channel_type || campaign.advertisingChannelType;
            return campaignType === selectedCampaignTypeFilter.type ||
                   String(campaignType) === String(selectedCampaignTypeFilter.type);
          })
          .map(c => String(c.id || c.campaignId));

        // Agréger les données quotidiennes des campagnes du type
        const aggregatedByDate = {};
        Object.entries(dailyDataByCampaign).forEach(([campaignId, dailyData]) => {
          if (filteredCampaignIds.includes(String(campaignId))) {
            dailyData.forEach(day => {
              if (!aggregatedByDate[day.date]) {
                aggregatedByDate[day.date] = { date: day.date, impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 };
              }
              aggregatedByDate[day.date].impressions += day.impressions || 0;
              aggregatedByDate[day.date].clicks += day.clicks || 0;
              aggregatedByDate[day.date].cost += day.cost || 0;
              aggregatedByDate[day.date].conversions += day.conversions || 0;
              aggregatedByDate[day.date].conversionsValue += day.conversionsValue || 0;
            });
          }
        });

        // Enrichir et agréger
        const enrichedTypeData = Object.values(aggregatedByDate)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .map(dayData => {
            const date = new Date(dayData.date);
            const weekStart = new Date(date);
            const dayOfWeek = date.getDay();
            const daysToMonday = (dayOfWeek === 0 ? 6 : dayOfWeek - 1);
            weekStart.setDate(date.getDate() - daysToMonday);
            weekStart.setHours(0, 0, 0, 0);

            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const quarter = Math.ceil((date.getMonth() + 1) / 3);
            const quarterKey = `${date.getFullYear()}-Q${quarter}`;
            const yearKey = String(date.getFullYear());

            const weekStartStr = formatLocalDate(weekStart);
            return {
              ...dayData,
              periods: {
                day: dayData.date,
                week: weekStartStr,
                month: monthKey,
                quarter: quarterKey,
                year: yearKey
              },
              labels: {
                day: date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
                week: `Sem. ${weekStartStr}`,
                month: `${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`,
                quarter: quarterKey,
                year: yearKey
              }
            };
          });

        if (enrichedTypeData.length > 0) {
          finalData = aggregateEnrichedDataWithPeriod(enrichedTypeData, granularity, periodData);
        } else {
          finalData = aggregatedData;
        }
      } else {
        finalData = aggregatedData;
      }

    }

    // Calculer les KPI données
    let campaignsData = autoCharts.rawData ? 
      Object.values(autoCharts.rawData).find(data => data.campaigns)?.campaigns || [] : [];
    
    // Filtrer par campagne et/ou type de campagne si sélectionné
    if (selectedCampaignFilter) {
      campaignsData = campaignsData.filter(campaign => {
        const matches = campaign.id === selectedCampaignFilter.id || 
                       campaign.campaignId === selectedCampaignFilter.id ||
                       String(campaign.id) === String(selectedCampaignFilter.id) ||
                       campaign.name === selectedCampaignFilter.name;
        return matches;
      });
    }
    
    if (selectedCampaignTypeFilter) {
      campaignsData = campaignsData.filter(campaign => {
        const campaignType = campaign.type || campaign.campaignType || campaign.channelType || 
                            campaign.advertising_channel_type || campaign.advertisingChannelType;
        const matches = campaignType === selectedCampaignTypeFilter.type ||
                       String(campaignType) === String(selectedCampaignTypeFilter.type) ||
                       campaignType === selectedCampaignTypeFilter.name ||
                       String(campaignType) === String(selectedCampaignTypeFilter.name);
        return matches;
      });
    }
    
    // Si une campagne ou un type de campagne est filtré, utiliser les données du graphique filtré, sinon les métriques globales
    let totalClicks, totalConversions, totalCost, totalConversionsValue, totalInteractions, totalReactions, totalImpressions;
    // Les achats, comptés à part des conversions configurées (voir la carte `transactions`).
    let totalTransactions = 0;
    // Grandeurs vidéo sommables ; le taux et le CPV s'en dérivent plus bas.
    let totalViews, totalVideoImpressions, totalViewCost;
    /**
     * Couverture et répétition (Meta) : **jamais additionnées**, ni entre campagnes ni entre
     * jours. Ce sont des nombres de personnes, pas d'événements. Seule la valeur calculée par
     * Meta pour le périmètre entier a un sens ; partout ailleurs, `null`, et la carte affiche
     * un tiret plutôt qu'un chiffre faux.
     */
    let totalCouverture = null, totalRepetition = null;

    if ((selectedCampaignFilter || selectedCampaignTypeFilter) && campaignsData.length > 0) {
      // IMPORTANT: Utiliser directement les conversions de la campagne filtrée pour la cohérence
      totalClicks = campaignsData.reduce((sum, campaign) => sum + (campaign.clicks || 0), 0);
      totalConversions = campaignsData.reduce((sum, campaign) => sum + (campaign.conversions || 0), 0);
      totalTransactions = campaignsData.reduce((sum, campaign) => sum + (campaign.transactions || 0), 0);
      totalCost = campaignsData.reduce((sum, campaign) => sum + (campaign.cost || 0), 0);
      totalConversionsValue = campaignsData.reduce((sum, campaign) => sum + (campaign.conversionsValue || 0), 0);
      totalInteractions = campaignsData.reduce((sum, campaign) => sum + (campaign.interactions || 0), 0);
      totalReactions = campaignsData.reduce((sum, campaign) => sum + (campaign.reactions || 0), 0);
      totalImpressions = campaignsData.reduce((sum, campaign) => sum + (campaign.impressions || 0), 0);
      totalViews = campaignsData.reduce((sum, campaign) => sum + (campaign.views || 0), 0);
      totalVideoImpressions = campaignsData.reduce((sum, campaign) => sum + (campaign.videoImpressions || 0), 0);
      totalViewCost = campaignsData.reduce((sum, campaign) => sum + (campaign.viewCost || 0), 0);
      // Une sélection de campagnes n'a pas de couverture dédupliquée : Meta ne la calcule que
      // pour l'ensemble interrogé, et cet écran n'a pas de quoi la lui redemander.
      totalCouverture = null;
      totalRepetition = null;

    } else {
      // PRIORITÉ: Utiliser les métriques précalculées du backend (cohérentes avec Types de campagnes)
      const backendMetrics = autoCharts?.rawData ?
        Object.values(autoCharts.rawData)[0]?.metrics : null;

      if (backendMetrics) {
        // Utiliser les totaux du backend pour cohérence avec l'endpoint campaign-types
        totalClicks = backendMetrics.totalClicks || 0;
        totalConversions = backendMetrics.totalConversions || 0;
        totalTransactions = backendMetrics.totalTransactions || 0;
        totalCost = backendMetrics.totalCost || 0;
        totalConversionsValue = backendMetrics.totalConversionsValue || 0;
        totalInteractions = backendMetrics.totalInteractions || 0;
        totalReactions = backendMetrics.totalReactions || 0;
        totalImpressions = backendMetrics.totalImpressions || 0;
        totalViews = backendMetrics.views || 0;
        totalVideoImpressions = backendMetrics.videoImpressions || 0;
        totalViewCost = backendMetrics.viewCost || 0;
        // Sans `|| 0` : le serveur rend `null` quand Meta n'a pas pu fournir la valeur, et un
        // zéro se lirait comme une absence de diffusion.
        totalCouverture = Number.isFinite(backendMetrics.totalReach) ? backendMetrics.totalReach : null;
        totalRepetition = Number.isFinite(backendMetrics.frequency) ? backendMetrics.frequency : null;
      } else {
        // Fallback: calculer depuis les données agrégées si pas de métriques backend
        totalClicks = finalData.reduce((sum, period) => sum + (period.clicks || 0), 0);
        totalConversions = finalData.reduce((sum, period) => sum + (period.conversions || 0), 0);
        totalTransactions = finalData.reduce((sum, period) => sum + (period.transactions || 0), 0);
        totalCost = finalData.reduce((sum, period) => sum + (period.cost || 0), 0);
        totalConversionsValue = finalData.reduce((sum, period) => sum + (period.conversionsValue || 0), 0);
        totalInteractions = finalData.reduce((sum, period) => sum + (period.interactions || 0), 0);
        totalReactions = finalData.reduce((sum, period) => sum + (period.reactions || 0), 0);
        totalImpressions = finalData.reduce((sum, period) => sum + (period.impressions || 0), 0);
        totalViews = finalData.reduce((sum, period) => sum + (period.views || 0), 0);
        totalVideoImpressions = finalData.reduce((sum, period) => sum + (period.videoImpressions || 0), 0);
        totalViewCost = finalData.reduce((sum, period) => sum + (period.viewCost || 0), 0);
      }

    }

    const conversionRate = totalClicks > 0 ? (totalConversions / totalClicks * 100) : 0;
    const cpc = totalClicks > 0 ? (totalCost / totalClicks) : 0;
    const cpa = totalConversions > 0 ? (totalCost / totalConversions) : 0;
    // Seuil minimum de 0.1€ pour éviter des COS aberrants avec des valeurs très petites
    const cos = totalConversionsValue >= 0.1 ? (totalCost / totalConversionsValue * 100) : 0;
    const coutParInteraction = totalInteractions > 0 ? (totalCost / totalInteractions) : 0;
    const coutParReaction = totalReactions > 0 ? (totalCost / totalReactions) : 0;
    const vtr = totalVideoImpressions > 0 ? (totalViews / totalVideoImpressions * 100) : 0;
    const cpv = totalViews > 0 ? (totalViewCost / totalViews) : 0;

    // Calculer les données de période précédente filtrées par campagne si applicable
    let computedPreviousPeriodData = previousPeriodData;

    // Debug: vérifier les données de comparaison reçues
    if (previousPeriodData) {
      console.log('📊 [PerformanceChart] previousPeriodData reçu:', {
        totalClicks: previousPeriodData.totalClicks,
        totalConversions: previousPeriodData.totalConversions,
        totalCost: previousPeriodData.totalCost,
        keys: Object.keys(previousPeriodData)
      });
    }

    if ((selectedCampaignFilter || selectedCampaignTypeFilter) && previousPeriodData?.dailyDataByCampaign) {
      const prevDailyDataByCampaign = previousPeriodData.dailyDataByCampaign;

      let campaignIdsToInclude = [];

      if (selectedCampaignFilter) {
        // Filtrage par campagne spécifique
        const campaignId = selectedCampaignFilter.id || selectedCampaignFilter.campaignId;
        campaignIdsToInclude = [String(campaignId)];
      } else if (selectedCampaignTypeFilter) {
        // Filtrage par type de campagne
        const campaignsData = autoCharts.rawData ?
          Object.values(autoCharts.rawData).find(data => data.campaigns)?.campaigns || [] : [];
        campaignIdsToInclude = campaignsData
          .filter(campaign => {
            const campaignType = campaign.type || campaign.advertisingChannelType;
            // Comparer avec l'objet filtre {type, name} ou directement la valeur
            const filterType = selectedCampaignTypeFilter?.type ?? selectedCampaignTypeFilter;
            const filterName = selectedCampaignTypeFilter?.name;
            return campaignType === filterType ||
                   String(campaignType) === String(filterType) ||
                   campaignType === filterName ||
                   String(campaignType) === String(filterName);
          })
          .map(c => String(c.id || c.campaignId));
      }

      // Agréger les métriques des campagnes filtrées
      let prevTotalImpressions = 0;
      let prevTotalClicks = 0;
      let prevTotalConversions = 0;
      let prevTotalCost = 0;
      let prevTotalConversionsValue = 0;
      let prevTotalInteractions = 0;
      let prevTotalReactions = 0;
      // Les interactions sociales, comparées comme les autres : sans elles, la carte
      // afficherait sa valeur sans variation, seule de la ligne à ne rien dire du passé.
      let prevTotalComments = 0;
      let prevTotalSaves = 0;
      let prevTotalShares = 0;
      let prevTotalPageLikes = 0;

      Object.entries(prevDailyDataByCampaign).forEach(([campaignId, dailyData]) => {
        if (campaignIdsToInclude.includes(String(campaignId))) {
          dailyData.forEach(day => {
            prevTotalImpressions += day.impressions || 0;
            prevTotalClicks += day.clicks || 0;
            prevTotalConversions += day.conversions || 0;
            prevTotalCost += day.cost || 0;
            prevTotalConversionsValue += day.conversionsValue || 0;
            prevTotalInteractions += day.interactions || 0;
            prevTotalReactions += day.reactions || 0;
            prevTotalComments += day.comments || 0;
            prevTotalSaves += day.saves || 0;
            prevTotalShares += day.shares || 0;
            prevTotalPageLikes += day.pageLikes || 0;
          });
        }
      });

      const prevCtr = prevTotalImpressions > 0 ? (prevTotalClicks / prevTotalImpressions * 100) : 0;
      const prevConversionRate = prevTotalClicks > 0 ? (prevTotalConversions / prevTotalClicks * 100) : 0;
      const prevCpc = prevTotalClicks > 0 ? (prevTotalCost / prevTotalClicks) : 0;
      const prevCpa = prevTotalConversions > 0 ? (prevTotalCost / prevTotalConversions) : 0;
      // Seuil minimum de 0.1€ pour éviter des COS aberrants
      const prevCos = prevTotalConversionsValue >= 0.1 ? (prevTotalCost / prevTotalConversionsValue * 100) : 0;
      const prevCoutParInteraction = prevTotalInteractions > 0 ? (prevTotalCost / prevTotalInteractions) : 0;
      const prevCoutParReaction = prevTotalReactions > 0 ? (prevTotalCost / prevTotalReactions) : 0;

      computedPreviousPeriodData = {
        ...previousPeriodData,
        totalImpressions: prevTotalImpressions,
        totalClicks: prevTotalClicks,
        totalConversions: prevTotalConversions,
        totalCost: prevTotalCost,
        totalConversionsValue: prevTotalConversionsValue,
        totalInteractions: prevTotalInteractions,
        totalReactions: prevTotalReactions,
        totalComments: prevTotalComments,
        totalSaves: prevTotalSaves,
        totalShares: prevTotalShares,
        totalPageLikes: prevTotalPageLikes,
        ctr: prevCtr,
        conversionRate: prevConversionRate,
        cpc: prevCpc,
        cpa: prevCpa,
        cos: prevCos,
        coutParInteraction: prevCoutParInteraction,
        coutParReaction: prevCoutParReaction
      };

      console.log(`📊 [PREVIOUS-PERIOD-FILTER] Données période précédente filtrées:`, {
        campaignIdsToInclude,
        prevTotalClicks,
        prevTotalConversions,
        prevTotalCost
      });
    }

    return {
      finalChartData: finalData,
      kpiData: {
        totalClicks,
        totalConversions,
        totalTransactions,
        totalCost,
        totalConversionsValue,
        totalInteractions,
        totalReactions,
        totalImpressions,
        conversionRate,
        cpc,
        cpa,
        cos,
        coutParInteraction,
        coutParReaction,
        views: totalViews || 0,
        vtr,
        cpv,
        totalCouverture,
        totalRepetition
      },
      filteredPreviousPeriodData: computedPreviousPeriodData
    };
  }, [memoizedChartData, granularity, autoCharts, selectedCampaignFilter, selectedCampaignTypeFilter, previousPeriodData]);

  // Préparer les données du graphique avec la métrique sélectionnée pour la courbe
  const chartDataWithLineMetric = useMemo(() => {
    return finalChartData.map(d => ({
      ...d,
      // `sansMesure` : période comblée hors de la plage réellement mesurée. `null` plutôt que
      // zéro, sans quoi `d.clicks || 0` et ses voisins reposeraient la courbe sur l'axe.
      lineMetricValue: d.sansMesure ? null : getLineMetricValue(d),
      barMetricValue: d.sansMesure ? null : getBarMetricValue(d)
    }));
  }, [finalChartData, selectedLineMetric, selectedBarMetric]);

  // Configuration actuelle de la métrique de la courbe (avec fallback sur ctr)
  const currentLineConfig = lineMetricConfig[selectedLineMetric] || lineMetricConfig.ctr;

  // Déterminer quelle métrique afficher en barres selon selectedBarMetric
  // Configuration des barres (pour TOUTES les métriques : VALEURS + RATIOS)
  const barMetricConfig = useMemo(() => {
    // Utilise barMetricValue qui est calculé pour chaque point de données
    const configs = {
      clicks: { name: 'Clics', fill: '#82ca9d' },
      impressions: { name: 'Impressions', fill: '#6366f1' },
      cost: { name: 'Coût', fill: '#ef4444' },
      conversionsValue: { name: 'Valeur Conv.', fill: '#10b981' },
      interactions: { name: 'Interactions', fill: '#06b6d4' },
      reactions: { name: 'Réactions', fill: '#a855f7' },
      // Sans ces quatre entrées, choisir une interaction sociale pour l'histogramme
      // laissait une barre sans nom ni couleur.
      comments: { name: 'Commentaires', fill: '#14b8a6' },
      saves: { name: 'Enregistrements', fill: '#0ea5e9' },
      shares: { name: 'Partages', fill: '#f43f5e' },
      pageLikes: { name: "J'aime (Facebook)", fill: '#7c3aed' },
      conversions: { name: 'Conversions', fill: '#8884d8' },
      // RATIOs
      ctr: { name: 'CTR', fill: '#14b8a6' },
      conversionRate: { name: 'Taux conv.', fill: '#f59e0b' },
      cpc: { name: 'CPC', fill: '#8b5cf6' },
      cpa: { name: 'CPA', fill: '#f97316' },
      cos: { name: 'COS', fill: '#ec4899' },
      panierMoyen: { name: 'Panier moyen', fill: '#6366f1' },
      coutParInteraction: { name: 'Coût/Inter.', fill: '#0891b2' },
      coutParReaction: { name: 'Coût/Réact.', fill: '#9333ea' },
      roas: { name: 'ROAS', fill: '#22c55e' },
      roi: { name: 'ROI', fill: '#3b82f6' }
    };
    const config = configs[selectedBarMetric] || configs.conversions;
    return { dataKey: 'barMetricValue', ...config };
  }, [selectedBarMetric]);

  // Configuration des KPI cards pour le rendu dynamique
  /**
   * Une série pour la courbe d'une métrique **par personne**, ou rien.
   *
   * Ces métriques n'ont de valeur qu'au jour le jour. Trois cas, et ils se distinguent :
   *
   * - **`null`** : le point regroupe plusieurs journées, et sa couverture n'existe pas. Un
   *   seul suffit à retirer la courbe — la tracer reviendrait à inventer des valeurs.
   * - **`undefined`** : le champ n'est pas porté par ce point. C'est le cas d'un jour comblé
   *   sur un chemin qui ne connaît pas la métrique ; sans aucun nombre dans la série, il n'y
   *   a rien à montrer.
   * - **un nombre** : la couverture de cette journée, telle que Meta l'a dédupliquée.
   *
   * Un jour sans diffusion vaut zéro, ce qui est une mesure et non un trou : les `undefined`
   * restants sont donc ramenés à zéro dès lors qu'au moins un point porte un nombre.
   */
  const serieParJour = useCallback((champ) => {
    const serie = finalChartData.map(point => point[champ]);
    if (serie.length === 0) return [];
    if (serie.some(v => v === null)) return [];
    if (!serie.some(v => typeof v === 'number')) return [];
    return serie.map(v => (typeof v === 'number' ? v : 0));
  }, [finalChartData]);

  const kpiConfig = useMemo(() => ({
    clics: {
      title: 'Clics',
      getValue: () => kpiData.totalClicks,
      color: '#82ca9d',
      chartMetric: 'clicks',
      isBarMetric: true, // VALEUR → histogramme
      getCurrentPeriodData: () => finalChartData.map(d => d.clicks),
      getPreviousValue: () => filteredPreviousPeriodData?.totalClicks
    },
    impressions: {
      title: 'Impressions',
      getValue: () => kpiData.totalImpressions,
      color: '#6366f1',
      chartMetric: 'impressions',
      isBarMetric: true, // VALEUR → histogramme
      getCurrentPeriodData: () => finalChartData.map(d => d.impressions || 0),
      getPreviousValue: () => filteredPreviousPeriodData?.totalImpressions
    },
    ctr: {
      title: 'CTR',
      getValue: () => {
        return kpiData.totalImpressions > 0 ? (kpiData.totalClicks / kpiData.totalImpressions * 100) : 0;
      },
      unit: '%',
      color: '#14b8a6',
      chartMetric: 'ctr',
      isBarMetric: false,
      getCurrentPeriodData: () => finalChartData.map(d => d.impressions > 0 ? (d.clicks / d.impressions * 100) : 0),
      getPreviousValue: () => filteredPreviousPeriodData?.ctr,
      formatter: (val) => formatNumber(val, 2)
    },
    taux_conv: {
      /*
       * Le titre suit le réglage, comme celui de `conv` juste en dessous : son numérateur est
       * la métrique Conversions. L'appeler « Taux de Conversion » tout court, à côté d'une
       * carte Conversions qui nomme ce qu'elle compte, laissait croire à deux mesures sans
       * rapport. `conversionConfig` vaut `null` hors Meta, et le titre reste alors inchangé.
       */
      title: conversionConfig ? libelleTauxConversion(conversionConfig, null, 'fbads') : 'Taux de Conversion',
      getValue: () => kpiData.conversionRate,
      unit: '%',
      color: '#f59e0b',
      chartMetric: 'conversionRate',
      isBarMetric: false,
      getCurrentPeriodData: () => finalChartData.map(d => (d.clicks > 0 ? (d.conversions || 0) / d.clicks * 100 : 0)),
      getPreviousValue: () => filteredPreviousPeriodData?.conversionRate,
      formatter: (val) => formatNumber(val, 2)
    },
    conv: {
      /*
       * ⚠️ **Le titre n'est pas figé.** La métrique Conversions compte ce que l'utilisateur a
       * configuré — « Conversions (Ajout au panier) » et non « Conversions ». Sans ce nom, deux
       * comptes du même écran afficheraient le même intitulé sur deux mesures différentes.
       * `conversionConfig` vaut `null` hors Meta : le libellé reste alors « Conversions ».
       */
      title: conversionConfig ? libelleConversions(conversionConfig, null, 'fbads') : 'Conversions',
      getValue: () => kpiData.totalConversions,
      color: '#8884d8',
      chartMetric: 'conversions',
      isBarMetric: true,
      getCurrentPeriodData: () => finalChartData.map(d => d.conversions || 0),
      getPreviousValue: () => filteredPreviousPeriodData?.totalConversions,
      formatter: (val) => formatNumber(val, 1)
    },
    /*
     * Les achats, à côté des conversions configurées.
     *
     * Elle n'est proposée que là où `COMPONENT_AVAILABLE_METRICS` la déclare — les écrans Meta
     * aujourd'hui. Sur les autres plateformes, les séries ne portent pas le champ et la carte
     * n'apparaît donc pas : offrir une métrique sans source afficherait un zéro permanent.
     */
    transactions: {
      title: 'Transactions',
      getValue: () => kpiData.totalTransactions,
      color: '#0ea5e9',
      chartMetric: 'transactions',
      isBarMetric: true,
      getCurrentPeriodData: () => finalChartData.map(d => d.transactions || 0),
      getPreviousValue: () => filteredPreviousPeriodData?.totalTransactions,
      formatter: (val) => formatNumber(val, 1)
    },
    cout: {
      title: 'Coût',
      getValue: () => kpiData.totalCost,
      unit: '€',
      color: '#ef4444',
      chartMetric: 'cost',
      isBarMetric: true, // VALEUR → histogramme
      getCurrentPeriodData: () => finalChartData.map(d => d.cost),
      getPreviousValue: () => filteredPreviousPeriodData?.totalCost,
      formatter: (val) => formatNumber(val, 2)
    },
    cpc: {
      title: 'CPC',
      getValue: () => kpiData.cpc,
      unit: '€',
      color: '#8b5cf6',
      chartMetric: 'cpc',
      isBarMetric: false,
      getCurrentPeriodData: () => finalChartData.map(d => d.clicks > 0 ? d.cost / d.clicks : 0),
      getPreviousValue: () => filteredPreviousPeriodData?.cpc,
      formatter: (val) => formatNumber(val, 2)
    },
    cpa: {
      title: 'CPA',
      getValue: () => kpiData.cpa,
      unit: '€',
      color: '#f97316',
      chartMetric: 'cpa',
      isBarMetric: false,
      getCurrentPeriodData: () => finalChartData.map(d => (d.conversions || 0) > 0 ? d.cost / (d.conversions || 1) : 0),
      getPreviousValue: () => filteredPreviousPeriodData?.cpa,
      formatter: (val) => kpiData.cpa > 0 ? formatNumber(val, 2) : '-'
    },
    valeur_conv: {
      title: 'Valeur de Conversion',
      getValue: () => kpiData.totalConversionsValue,
      unit: '€',
      color: '#10b981',
      chartMetric: 'conversionsValue',
      isBarMetric: true,
      getCurrentPeriodData: () => finalChartData.map(d => {
        const convValue = d.conversionsValue || 0;
        return convValue > 0 ? convValue : (d.conversions || 0) * 50;
      }),
      getPreviousValue: () => filteredPreviousPeriodData?.totalConversionsValue,
      formatter: (val) => formatNumber(val, 2)
    },
    cos: {
      title: 'COS',
      getValue: () => kpiData.cos,
      unit: '%',
      getColor: () => kpiData.cos <= getCosThreshold(selectedAccount?.id || 'global') ? '#10b981' : '#ef4444',
      chartMetric: 'cos',
      isBarMetric: false,
      getCurrentPeriodData: () => finalChartData.map(d => {
        const convValue = d.conversionsValue || ((d.conversions || 0) * 50);
        return convValue >= 0.1 ? (d.cost / convValue * 100) : 0;
      }),
      getPreviousValue: () => filteredPreviousPeriodData?.cos,
      formatter: (val) => formatNumber(val, 1)
    },
    panier_moyen: {
      title: 'Panier moyen',
      getValue: () => kpiData.totalConversions > 0 ? kpiData.totalConversionsValue / kpiData.totalConversions : 0,
      unit: '€',
      color: '#6366f1',
      chartMetric: 'panierMoyen',
      isBarMetric: false,
      getCurrentPeriodData: () => finalChartData.map(d => {
        const conv = d.conversions || 0;
        const convValue = d.conversionsValue || 0;
        return conv > 0 ? convValue / conv : 0;
      }),
      getPreviousValue: () => {
        const prevConv = filteredPreviousPeriodData?.totalConversions || 0;
        const prevValue = filteredPreviousPeriodData?.totalConversionsValue || 0;
        return prevConv > 0 ? prevValue / prevConv : 0;
      },
      formatter: (val) => val > 0 ? formatNumber(val, 2) : '-'
    },
    interactions: {
      title: 'Interactions',
      getValue: () => kpiData.totalInteractions,
      color: '#06b6d4',
      chartMetric: 'interactions',
      isBarMetric: true,
      getCurrentPeriodData: () => finalChartData.map(d => d.interactions || 0),
      getPreviousValue: () => filteredPreviousPeriodData?.totalInteractions
    },
    cout_par_interaction: {
      title: 'Coût/Inter.',
      getValue: () => kpiData.coutParInteraction,
      unit: '€',
      color: '#0891b2',
      chartMetric: 'coutParInteraction',
      isBarMetric: false,
      getCurrentPeriodData: () => finalChartData.map(d => (d.interactions || 0) > 0 ? (d.cost || 0) / d.interactions : 0),
      getPreviousValue: () => filteredPreviousPeriodData?.coutParInteraction,
      formatter: (val) => kpiData.coutParInteraction > 0 ? formatNumber(val, 2) : '-'
    },
    /**
     * Couverture et répétition — **sans série de graphique**, et c'est voulu.
     *
     * La série quotidienne de ce module ne porte pas la couverture, et elle ne le pourrait pas
     * utilement : replier des couvertures journalières en semaines ou en mois reviendrait à les
     * additionner, ce qui compte plusieurs fois la personne revenue plusieurs jours. Seul le
     * total de la période, calculé par Meta, a un sens — c'est celui qu'affiche la carte.
     *
     * `chartMetric` absent : la carte n'est donc pas cliquable (voir le garde à l'`onClick`).
     */
    couverture: {
      title: 'Couverture',
      getValue: () => kpiData.totalCouverture,
      color: '#6366f1',
      isBarMetric: false,
      formatter: (val) => (val == null ? '—' : formatNumber(val)),
      /**
       * Une courbe **seulement au jour le jour**.
       *
       * Chaque point est la couverture que Meta a dédupliquée pour cette journée : juste, et
       * lisible comme une courbe. Dès que les points regroupent plusieurs jours — semaine,
       * mois —, cette couverture n'existe plus : une personne touchée mardi et jeudi compterait
       * deux fois, et seul Meta saurait la recalculer période par période. Le serveur rend
       * alors `null`, et l'absence de nombre vaut absence de courbe, plutôt qu'une ligne à
       * zéro qui se lirait comme une absence de diffusion.
       */
      getCurrentPeriodData: () => serieParJour('reach'),
      // La comparaison n'existe que si le serveur a pu rendre une couverture dédupliquée pour
      // la période précédente — c'est-à-dire hors filtre campagne.
      getPreviousValue: () => filteredPreviousPeriodData?.totalReach ?? undefined
    },
    repetition: {
      title: 'Répétition',
      getValue: () => kpiData.totalRepetition,
      color: '#8b5cf6',
      isBarMetric: false,
      formatter: (val) => (val == null ? '—' : formatNumber(val, 2)),
      getCurrentPeriodData: () => serieParJour('frequency'),
      getPreviousValue: () => filteredPreviousPeriodData?.frequency ?? undefined
    },
    reactions: {
      title: 'Réactions',
      getValue: () => kpiData.totalReactions,
      color: '#a855f7',
      chartMetric: 'reactions',
      isBarMetric: true,
      getCurrentPeriodData: () => finalChartData.map(d => d.reactions || 0),
      getPreviousValue: () => filteredPreviousPeriodData?.totalReactions
    },
    /**
     * Les quatre interactions sociales.
     *
     * Sans entrée ici, la carte est **écartée en silence** : la liste des KPI filtre sur
     * `kpiConfig[key]`, si bien qu'une métrique cochée sans configuration ne s'affiche pas
     * — ou s'affiche à zéro ailleurs, ce qui est pire, car on la croit mesurée.
     *
     * Le total vient de la somme des jours, comme les réactions : la série quotidienne porte
     * ces champs depuis que le résumé les cumule.
     */
    commentaires: {
      title: 'Commentaires',
      getValue: () => finalChartData.reduce((t, d) => t + (d.comments || 0), 0),
      color: '#14b8a6',
      chartMetric: 'comments',
      isBarMetric: true,
      getCurrentPeriodData: () => finalChartData.map(d => d.comments || 0),
      getPreviousValue: () => filteredPreviousPeriodData?.totalComments
    },
    enregistrements: {
      title: 'Enregistrements',
      getValue: () => finalChartData.reduce((t, d) => t + (d.saves || 0), 0),
      color: '#0ea5e9',
      chartMetric: 'saves',
      isBarMetric: true,
      getCurrentPeriodData: () => finalChartData.map(d => d.saves || 0),
      getPreviousValue: () => filteredPreviousPeriodData?.totalSaves
    },
    partages: {
      title: 'Partages',
      getValue: () => finalChartData.reduce((t, d) => t + (d.shares || 0), 0),
      color: '#f43f5e',
      chartMetric: 'shares',
      isBarMetric: true,
      getCurrentPeriodData: () => finalChartData.map(d => d.shares || 0),
      getPreviousValue: () => filteredPreviousPeriodData?.totalShares
    },
    jaime_facebook: {
      title: "J'aime (Facebook)",
      getValue: () => finalChartData.reduce((t, d) => t + (d.pageLikes || 0), 0),
      color: '#7c3aed',
      chartMetric: 'pageLikes',
      isBarMetric: true,
      getCurrentPeriodData: () => finalChartData.map(d => d.pageLikes || 0),
      getPreviousValue: () => filteredPreviousPeriodData?.totalPageLikes
    },
    cout_par_reaction: {
      title: 'Coût/Réact.',
      getValue: () => kpiData.coutParReaction,
      unit: '€',
      color: '#9333ea',
      chartMetric: 'coutParReaction',
      isBarMetric: false,
      getCurrentPeriodData: () => finalChartData.map(d => (d.reactions || 0) > 0 ? (d.cost || 0) / d.reactions : 0),
      getPreviousValue: () => filteredPreviousPeriodData?.coutParReaction,
      formatter: (val) => kpiData.coutParReaction > 0 ? formatNumber(val, 2) : '-'
    },
    roas: {
      title: 'ROAS',
      getValue: () => kpiData.totalCost > 0 ? (kpiData.totalConversionsValue / kpiData.totalCost) * 100 : 0,
      unit: '%',
      color: '#22c55e',
      chartMetric: 'roas',
      isBarMetric: false,
      getCurrentPeriodData: () => finalChartData.map(d => d.cost > 0 ? ((d.conversionsValue || 0) / d.cost) * 100 : 0),
      getPreviousValue: () => {
        const prevCost = filteredPreviousPeriodData?.totalCost || 0;
        const prevValue = filteredPreviousPeriodData?.totalConversionsValue || 0;
        return prevCost > 0 ? (prevValue / prevCost) * 100 : 0;
      },
      formatter: (val) => val > 0 ? formatNumber(val, 0) : '-'
    },
    roi: {
      title: 'ROI',
      getValue: () => (kpiData.totalCost > 0 && kpiData.totalConversionsValue > 0) ? (kpiData.totalConversionsValue - kpiData.totalCost) / kpiData.totalCost : 0,
      unit: 'x',
      color: '#3b82f6',
      chartMetric: 'roi',
      isBarMetric: false,
      getCurrentPeriodData: () => finalChartData.map(d => (d.cost > 0 && (d.conversionsValue || 0) > 0) ? ((d.conversionsValue || 0) - d.cost) / d.cost : 0),
      getPreviousValue: () => {
        const prevCost = filteredPreviousPeriodData?.totalCost || 0;
        const prevValue = filteredPreviousPeriodData?.totalConversionsValue || 0;
        return (prevCost > 0 && prevValue > 0) ? (prevValue - prevCost) / prevCost : 0;
      },
      formatter: (val) => (kpiData.totalCost > 0 && kpiData.totalConversionsValue > 0) ? formatNumber(val, 2) : '-'
    },
    /**
     * Vidéo TrueView. Les trois cartes restent vides sur un compte sans vidéo — le backend
     * renvoie alors zéro partout — et le taux se rapporte aux impressions **vidéo**, jamais
     * aux impressions totales, d'où le dénominateur `videoImpressions` fourni à part.
     */
    views: {
      title: 'Vues',
      getValue: () => kpiData.views,
      unit: '',
      color: '#db2777',
      chartMetric: 'views',
      isBarMetric: true,
      getCurrentPeriodData: () => finalChartData.map(d => d.views || 0),
      getPreviousValue: () => filteredPreviousPeriodData?.views,
      formatter: (val) => formatNumber(val)
    },
    vtr: {
      title: 'Taux de vue',
      getValue: () => kpiData.vtr,
      unit: '%',
      color: '#e11d48',
      chartMetric: 'vtr',
      isBarMetric: false,
      getCurrentPeriodData: () => finalChartData.map(d => (d.videoImpressions || 0) > 0 ? ((d.views || 0) / d.videoImpressions * 100) : 0),
      getPreviousValue: () => filteredPreviousPeriodData?.vtr,
      formatter: (val) => kpiData.views > 0 ? formatNumber(val, 2) : '-'
    },
    cpv: {
      title: 'CPV',
      getValue: () => kpiData.cpv,
      unit: '€',
      color: '#be123c',
      chartMetric: 'cpv',
      isBarMetric: false,
      getCurrentPeriodData: () => finalChartData.map(d => (d.views || 0) > 0 ? (d.viewCost || 0) / d.views : 0),
      getPreviousValue: () => filteredPreviousPeriodData?.cpv,
      formatter: (val) => kpiData.views > 0 ? formatNumber(val, 3) : '-'
    }
  }), [kpiData, finalChartData, filteredPreviousPeriodData, formatNumber, selectedAccount?.id, serieParJour, conversionConfig]);

  // Pour les clients : masquer complètement si le module est fermé
  // Utiliser isClientModuleVisible si disponible, sinon isVisible
  const clientModuleVisible = isClientModuleVisible ? isClientModuleVisible(moduleId) : true;
  if (userRole === 'CLIENT' && (!isVisible || !clientModuleVisible)) {
    return null;
  }

  return (
    <div className="performance-chart-section">
      <div className="section-header">
        <h4><TrendingUp size={16} />&nbsp;Résultats</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Boutons de granularité */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {[
              { value: 'day', label: 'Jour' },
              { value: 'week', label: 'Semaine' },
              { value: 'month', label: 'Mois' },
              { value: 'quarter', label: 'Trimestre' },
              { value: 'year', label: 'Année' }
            ].map(({ value, label }) => (
              <button
                key={value}
                onClick={() => onGranularityChange?.(value)}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: granularity === value ? '#1a1a1a' : 'white',
                  color: granularity === value ? 'white' : '#374151',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                title={`Afficher par ${label.toLowerCase()}`}
              >
                {label}
              </button>
            ))}
          </div>
          {/* Bouton configuration des métriques - visible uniquement pour non-clients */}
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {isClientModuleVisible && toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible(moduleId)}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
            />
          ) : (
            <button
              onClick={handleToggle}
              className="section-toggle-btn"
              title={showPerformanceChart ? "Masquer la section" : "Afficher la section"}
            >
              {showPerformanceChart ? '−' : '+'}
            </button>
          )}
        </div>
      </div>
      {showPerformanceChart && (
        <>
          {/* Afficher un message informatif si pas de données réelles */}
          {/* Vérifier les deux sources de données: charts ET enrichedDailyData (pour Microsoft/Facebook Ads) */}
          {(!autoCharts?.charts?.find(chart => chart.id === 'real-performance-timeline')?.data?.length &&
            !Object.values(autoCharts?.rawData || {})[0]?.enrichedDailyData?.length) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px',
              backgroundColor: '#f3f4f6',
              borderRadius: '8px',
              marginBottom: '12px',
              color: '#6b7280',
              fontSize: '14px',
              border: '1px solid #e5e7eb'
            }}>
              <AlertCircle size={16} style={{ marginRight: '8px', opacity: 0.6 }} />
              Aucune donnée disponible - Affichage de la structure temporelle
            </div>
          )}
          <div className="full-width-chart" style={{
            width: '100%',
            minWidth: '300px',
            height: '400px',
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxSizing: 'border-box',
            overflow: 'visible'
          }}>
            <ResponsiveContainer width="100%" height="100%" debounce={50}>
              <ComposedChart
                data={chartDataWithLineMetric}
                margin={{ top: 20, right: 10, left: 10, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  type="category"
                  allowDuplicatedCategory={false}
                  tickFormatter={(value) => formatDateLabel(value, granularity)}
                />
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  stroke={currentLineConfig.color}
                  tick={{ fontSize: 12 }}
                  label={{ value: currentLineConfig.label, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#8884d8"
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Conversions', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name) => {
                    // Période sans mesure : ni zéro ni valeur formatée, un tiret.
                    if (value === null || value === undefined) return ['—', name];
                    if (name === currentLineConfig.label) {
                      return [currentLineConfig.format(value) + currentLineConfig.unit, name];
                    }
                    // Les conversions gardent leur décimale : Meta en rend de fractionnaires.
                    if (name === 'Conversions') return [formatNumber(value, 1), name];
                    /*
                      La série en barres n'avait aucun formatage propre : elle retombait sur
                      `formatNumber(value)`, c'est-à-dire un entier. Toute métrique de ratio
                      choisie pour l'histogramme s'affichait donc « 0 » dans l'infobulle — un
                      CPC de 0,0164 € ou un coût par interaction de 0,016 €. La carte, elle,
                      montrait 0,02 €, et les deux se contredisaient à l'écran.

                      Le formatage est repris de `lineMetricConfig`, la table qui porte déjà
                      l'unité et le nombre de décimales de chaque métrique. Une seconde table
                      propre aux barres aurait fini par diverger de la première.
                    */
                    const configBarre = lineMetricConfig[selectedBarMetric];
                    if (configBarre && name === barMetricConfig.name) {
                      return [configBarre.format(value) + configBarre.unit, name];
                    }
                    return [formatNumber(value), name];
                  }}
                />
                <Legend />

                {/* Repères des notes. Le mécanisme est partagé avec les graphiques
                    Google Analytics, Matomo et Piano : le regroupement par période, le
                    chevauchement des plages et le dédoublonnage sont trop subtils pour
                    vivre en deux exemplaires. */}
                {marqueursDeNotes({
                  notes,
                  donneesDuGraphique: chartDataWithLineMetric,
                  granularite: granularity,
                  /* Les bornes réelles du graphique, que ses étiquettes ne donnent plus aux
                     granularités larges : une colonne « 2026 » porte le nom de l'année entière
                     alors qu'on n'en montre parfois qu'une semaine. `memoizedChartData` les
                     calcule déjà pour filtrer ses données — on les relit, sans les refaire. */
                  periode: {
                    debut: memoizedChartData?.effectivePeriodData?.startDate,
                    fin: memoizedChartData?.effectivePeriodData?.endDate
                  },
                  onSurvol: (groupe, e) => {
                    setHoveredNote(groupe);
                    setNoteTooltipPosition({ x: e.clientX, y: e.clientY });
                  },
                  onSortie: () => setHoveredNote(null)
                })}

                {/* Barres: Conversions, Interactions ou Réactions selon la sélection */}
                <Bar
                  yAxisId="right"
                  dataKey={barMetricConfig.dataKey}
                  fill={barMetricConfig.fill}
                  fillOpacity={0.8}
                  name={barMetricConfig.name}
                  radius={[4, 4, 0, 0]}
                />

                {/* Métrique sélectionnée en courbe */}
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="lineMetricValue"
                  stroke={currentLineConfig.color}
                  strokeWidth={3}
                  name={currentLineConfig.label}
                  dot={{ fill: currentLineConfig.color, strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 7, fill: currentLineConfig.color }}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>

            {/* Tooltip pour les notes au survol */}
            {/* L'infobulle des notes, partagée avec les graphiques analytics. */}
            <InfobulleDeNotes survolee={hoveredNote} position={noteTooltipPosition} />
          </div>
          <div className="chart-insights" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '12px',
            marginTop: '16px'
          }}>
            {/* Rendu dynamique des KPI cards selon l'ordre configuré */}
            {componentMetrics.order.filter(key => isComponentMetricSelected(key) && kpiConfig[key]).map(metricKey => {
              const config = kpiConfig[metricKey];
              // Déterminer si la métrique est une VALEUR ou un RATIO
              const isValueMetric = VALUE_METRICS.includes(metricKey);
              const isRatioMetric = RATIO_METRICS.includes(metricKey);
              // Vérifier si cette métrique est sélectionnée pour l'histogramme ou la courbe
              const isSelectedForLine = selectedLineMetric === config.chartMetric;
              const isSelectedForBar = selectedBarMetric === config.chartMetric;
              const color = config.getColor ? config.getColor() : config.color;

              // Cliquable seulement si la métrique a une série à afficher dans le graphique
              const isClickable = (isValueMetric || isRatioMetric) && Boolean(config.chartMetric);

              // Style de l'outline : plein pour barres et courbe
              const outlineStyle = isSelectedForBar
                ? `3px solid ${color}`
                : isSelectedForLine
                  ? `3px solid ${color}`
                  : 'none';

              return (
                <div
                  key={metricKey}
                  onClick={() => {
                    /**
                     * Une carte sans métrique de graphique ne bascule rien.
                     *
                     * Sans ce garde, le clic posait `selectedBarMetric = undefined` et le
                     * graphique perdait ses barres — une carte purement informative ne doit pas
                     * pouvoir casser l'affichage.
                     */
                    if (!config.chartMetric) return;
                    if (selectedBarMetric === config.chartMetric) {
                      // Déjà en barres → intervertir : cette carte devient courbe, l'ancienne courbe devient barres
                      const oldLineMetric = selectedLineMetric;
                      setSelectedLineMetric(config.chartMetric);
                      if (oldLineMetric) {
                        setSelectedBarMetric(oldLineMetric);
                      }
                      return;
                    }
                    // La métrique cliquée va en barres
                    // L'ancienne métrique en barres passe en courbe
                    const oldBarMetric = selectedBarMetric;
                    setSelectedBarMetric(config.chartMetric);
                    if (oldBarMetric) {
                      setSelectedLineMetric(oldBarMetric);
                    }
                  }}
                  style={{
                    cursor: isClickable ? 'pointer' : 'default',
                    borderRadius: '8px',
                    outline: outlineStyle,
                    outlineOffset: '2px',
                    transition: 'outline 0.2s'
                  }}
                  /*
                   * ⚠️ **Deux choses se disputent le survol de cette carte.**
                   *
                   * L'indication d'interaction occupe l'attribut `title`, et elle y reste : elle
                   * est secondaire, et l'attente du navigateur ne la dessert pas.
                   *
                   * Ce que compte la mesure, lui, passe par la bulle maison — instantanée. Sur
                   * les deux cartes de conversion seulement : l'en-tête y annonce « 2 sources »
                   * sans dire lesquelles, et c'est la question qu'on se pose en survolant.
                   * Ailleurs, et sur un compte sans source choisie, `infobulleConversions` rend
                   * `null` et rien ne se monte.
                   */
                  title={!isClickable
                    ? undefined
                    : isSelectedForBar ? 'Affiché en barres' : isSelectedForLine ? 'Affiché en courbe' : 'Cliquez pour afficher en barres'}
                  onMouseEnter={survolerSources(
                    (config.chartMetric === 'conversions' || config.chartMetric === 'conversionRate')
                      ? infobulleConversions(conversionConfig)
                      : null
                  )}
                  onMouseLeave={quitterSources}
                >
                  <KPICard
                    title={config.title}
                    value={config.getValue()}
                    unit={config.unit}
                    color={color}
                    /**
                     * Appel gardé, pour la même raison que `getPreviousValue` ci-dessous : une
                     * carte peut n'avoir aucune série quotidienne à montrer — la couverture,
                     * qui ne se replie pas par jour, est dans ce cas. Sans garde, l'appel
                     * plantait tout le dashboard dès que la carte était cochée.
                     */
                    currentPeriodData={config.getCurrentPeriodData ? config.getCurrentPeriodData() : undefined}
                    /**
                     * Appel gardé : toutes les cartes n'ont pas de valeur de comparaison.
                     *
                     * Quatre d'entre elles — les interactions sociales — n'en déclaraient pas,
                     * et l'appel sans garde faisait planter tout le dashboard dès qu'une était
                     * cochée. Une carte sans comparaison est un cas normal, pas une erreur.
                     */
                    previousValue={config.getPreviousValue ? config.getPreviousValue() : undefined}
                    formatter={config.formatter}
                    comparisonLabel={comparisonLabel}
                    className={getMetricObjectiveClassName(metricKey, config.getValue(), 'results', selectedAccount?.id || 'global')}
                  />
                </div>
              );
            })}
            {/* Hors de la boucle : le même élément de portail rendu par chaque carte
                monterait autant de bulles qu'il y a de cartes cochées. */}
            {bulleSources}
          </div>
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId={moduleId}
        accountId={selectedAccount?.id || 'global'}
        componentTitle={moduleId.startsWith('msads_') ? 'Résultats Microsoft Ads' : moduleId.startsWith('fbads_') ? 'Résultats Facebook Ads' : moduleId.startsWith('tiktok_') ? 'Résultats TikTok Ads' : moduleId.startsWith('pinterest_') ? 'Résultats Pinterest Ads' : moduleId.startsWith('ga4_') ? 'Résultats Google Analytics' : 'Résultats'}
        /*
         * La définition de la métrique Conversions se règle dans l'engrenage de la métrique,
         * comme sur le dashboard Google Analytics. `conversionConfig` ne vaut quelque chose que
         * sur Meta : ailleurs la métrique n'est pas configurable, `METRICS_WITH_ADVANCED_OPTIONS`
         * ne déclare rien pour ces composants, et la modale n'affiche donc pas le réglage.
         *
         * Le rechargement est nécessaire : les séries sont calculées côté serveur avec
         * l'ancienne définition, et l'écran afficherait sinon le nouveau nom au-dessus des
         * anciens chiffres.
         */
        token={token}
        propertyId={selectedAccount?.id}
        currentAdvancedConfig={conversionConfig}
        onAdvancedConfigSave={() => window.location.reload()}
      />
    </div>
  );
};

export default PerformanceChart;